import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { getApiBase } from './utils/api'
import { parseHash, buildHash, navigate as routerNavigate, updateRoute, clearRouteParams, type RouteParams } from './utils/router'
import type { Product, AuthInfo, Analytics } from './types'
import {
  ProductCategories,
  ProductCategoryLabels,
  ProductStatuses,
  UserRoles,
  WorkOrderStatuses,
  WorkOrderTypes,
  Priorities,
  IoTDeviceStatuses,
  IoTDeviceTypes,
  type WorkOrder,
  type User,
  type UserRole,
  type UserInput,
  type IoTDevice,
  type ESGMetric,
  type MasterProduct,
  type Organisation,
  type OrganisationInput,
} from '@esf/shared'
import Modal, { AlertModal, ConfirmModal } from './components/Modal'
import ProductDetailModal from './components/ProductDetailModal'
import UserForm from './components/UserForm'
import DppTemplateDetailModal from './components/DppTemplateDetailModal'
import DppTemplateFormPage from './components/DppTemplateFormPage'
import ProductFormPage from './components/ProductFormPage'
import WorkOrderFormPage from './components/WorkOrderFormPage'
import PublicPassportPage from './components/dpp/PublicPassportPage'
import IoTDashboardPage from './components/IoTDashboardPage'
import ESGPage from './components/ESGPage'
import WorkOrdersPage from './components/WorkOrdersPage'
import DppTemplatesPage from './components/DppTemplatesPage'
import ProductsPage from './components/ProductsPage'
import UsersPage from './components/UsersPage'
import SettingsPage from './components/SettingsPage'
import DashboardPage from './components/DashboardPage'
import NotificationTray from './components/NotificationTray'
import LoginPage from './components/LoginPage'
import AdminDashboardPage from './components/AdminDashboardPage'
import OrganisationsPage from './components/OrganisationsPage'
import OrganisationForm from './components/OrganisationForm'
import OrganisationDetailModal from './components/OrganisationDetailModal'
import AuditLogsPage from './components/AuditLogsPage'
import TutorialPage from './components/TutorialPage'
import { type AuditLog } from '@esf/shared'

type Page = 'dashboard' | 'admin-dashboard' | 'organisations' | 'assets' | 'dpp-templates' | 'dpp-template-create' | 'dpp-template-edit' | 'product-create' | 'product-edit' | 'iot' | 'esg' | 'workorders' | 'workorder-create' | 'workorder-edit' | 'users' | 'settings' | 'audit-logs' | 'tutorial'

const validPages: Page[] = ['dashboard', 'admin-dashboard', 'organisations', 'assets', 'dpp-templates', 'dpp-template-create', 'dpp-template-edit', 'product-create', 'product-edit', 'iot', 'esg', 'workorders', 'workorder-create', 'workorder-edit', 'users', 'settings', 'audit-logs', 'tutorial']

// Parse route from URL using centralized router
const getRouteFromHash = (): RouteParams => parseHash()

// Get page from parsed route
const getPageFromHash = (): Page => {
  const route = getRouteFromHash()
  const page = route.page
  if (page === 'public-passport') return 'dashboard' // Special handling
  return validPages.includes(page as Page) ? (page as Page) : 'dashboard'
}

export default function App() {
  const base = useMemo(() => getApiBase(), [])

  // Initialize route state from URL
  const initialRoute = useMemo(() => getRouteFromHash(), [])

  // Public passport route detection
  const [publicPassportId, setPublicPassportId] = useState<string | null>(
    initialRoute.page === 'public-passport' ? initialRoute.id || null : null
  )

  // DPP template edit route detection
  const [editDppTemplateId, setEditDppTemplateId] = useState<string | null>(
    initialRoute.page === 'dpp-template-edit' ? initialRoute.id || null : null
  )

  // Product edit route detection
  const [editProductId, setEditProductId] = useState<string | null>(
    initialRoute.page === 'product-edit' ? initialRoute.id || null : null
  )

  // Work order edit route detection
  const [editWorkOrderId, setEditWorkOrderId] = useState<string | null>(
    initialRoute.page === 'workorder-edit' ? initialRoute.id || null : null
  )

  // Work order view route detection (deep link from notification)
  const [viewWorkOrderId, setViewWorkOrderId] = useState<string | null>(initialRoute.view || null)

  // Work order create with preselected asset
  const [workOrderCreateAssetId, setWorkOrderCreateAssetId] = useState<string | null>(
    initialRoute.page === 'workorder-create' ? initialRoute.asset || null : null
  )

  // Auth state
  const [user, setUser] = useState<AuthInfo | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authChecking, setAuthChecking] = useState(true)
  const [debugRole, setDebugRole] = useState<string>('SuperAdmin')
  const debugMode = import.meta.env.VITE_DEBUG_MODE === 'true'

  // Navigation - initialize from URL hash
  const [currentPage, setCurrentPage] = useState<Page>(getPageFromHash)

  // Navigate to a page (updates URL and state)
  const navigateTo = useCallback((page: Page, params?: string) => {
    const routeParams: RouteParams = { page }
    if (params) {
      routeParams.id = params
    }
    routerNavigate(routeParams)
    setCurrentPage(page)
    if (page === 'dpp-template-edit' && params) {
      setEditDppTemplateId(params)
    } else {
      setEditDppTemplateId(null)
    }
    if (page === 'product-edit' && params) {
      setEditProductId(params)
    } else {
      setEditProductId(null)
    }
    if (page === 'workorder-edit' && params) {
      setEditWorkOrderId(params)
    } else {
      setEditWorkOrderId(null)
    }
    // Reset workorder create asset when navigating away
    if (page !== 'workorder-create') {
      setWorkOrderCreateAssetId(null)
    }
  }, [])

  // Navigate to a page with preset filters (for dashboard quick links)
  const navigateWithFilters = useCallback((page: Page, filters: { status?: string; category?: string }) => {
    // Clear existing filters first, then set new ones
    setStatusFilter(filters.status || '')
    setCategoryFilter(filters.category || '')
    setSearchQuery('')
    // Navigate to the page
    routerNavigate({ page })
    setCurrentPage(page)
  }, [])

  // Navigate to workorder-create with preselected asset
  const navigateToWorkOrderCreate = useCallback((assetId?: string) => {
    if (assetId) {
      routerNavigate({ page: 'workorder-create', asset: assetId })
      setWorkOrderCreateAssetId(assetId)
    } else {
      routerNavigate({ page: 'workorder-create' })
      setWorkOrderCreateAssetId(null)
    }
    setCurrentPage('workorder-create')
  }, [])

  // Update URL with view parameter (for modals)
  const setUrlView = useCallback((viewId: string | null) => {
    if (viewId) {
      updateRoute({ view: viewId }, true)
    } else {
      clearRouteParams(['view'], true)
    }
  }, [])

  // Update URL with org parameter (for admin dashboard)
  const setUrlOrg = useCallback((orgId: string | null) => {
    if (orgId) {
      updateRoute({ org: orgId }, true)
    } else {
      clearRouteParams(['org'], true)
    }
  }, [])

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handleHashChange = () => {
      const route = getRouteFromHash()

      // Handle public passport
      if (route.page === 'public-passport') {
        setPublicPassportId(route.id || null)
        return
      }
      setPublicPassportId(null)

      // Update page state
      const page = validPages.includes(route.page as Page) ? (route.page as Page) : 'dashboard'
      setCurrentPage(page)

      // Update edit IDs
      setEditDppTemplateId(route.page === 'dpp-template-edit' ? route.id || null : null)
      setEditProductId(route.page === 'product-edit' ? route.id || null : null)
      setEditWorkOrderId(route.page === 'workorder-edit' ? route.id || null : null)
      setWorkOrderCreateAssetId(route.page === 'workorder-create' ? route.asset || null : null)

      // Handle view parameter (for opening modals from URL)
      if (route.view) {
        setViewWorkOrderId(route.view)
      }

      // Handle org parameter (for admin dashboard)
      if (route.org) {
        setSelectedAdminOrgId(route.org)
      }
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [])

  // If viewing public passport, render only that page
  if (publicPassportId) {
    return <PublicPassportPage passportId={publicPassportId} />
  }

  // Data state
  const [products, setProducts] = useState<Product[]>([])
  const [allProducts, setAllProducts] = useState<Product[]>([]) // Unfiltered products for Dashboard
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([])
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [iotDevices, setIotDevices] = useState<IoTDevice[]>([])
  const [esgMetrics, setEsgMetrics] = useState<ESGMetric[]>([])
  const [organisations, setOrganisations] = useState<Organisation[]>([])
  const [adminDashboardStats, setAdminDashboardStats] = useState<{
    totalOrganisations: number
    totalAssets: number
    totalUsers: number
    totalWorkOrders: number
    totalIoTDevices: number
    byOrganisation: Array<{
      id: string
      name: string
      slug: string
      assetCount: number
      userCount: number
      workOrderCount: number
      iotDeviceCount: number
    }>
  } | null>(null)
  const [orgStats, setOrgStats] = useState<Record<string, {
    organisationId: string
    assetCount: number
    userCount: number
    workOrderCount: number
    iotDeviceCount: number
  }>>({})
  const [selectedAdminOrgId, setSelectedAdminOrgId] = useState<string | null>(initialRoute.org || null)
  const [selectedUserOrgFilter, setSelectedUserOrgFilter] = useState<string | null>(null)
  const [adminDashboardLoading, setAdminDashboardLoading] = useState(false)
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [auditLogsLoading, setAuditLogsLoading] = useState(false)
  const [auditNextKey, setAuditNextKey] = useState<string | null>(null)
  const [auditActionFilter, setAuditActionFilter] = useState('')
  const [auditActorFilter, setAuditActorFilter] = useState('')
  const [auditFromDate, setAuditFromDate] = useState('')
  const [auditToDate, setAuditToDate] = useState('')
  const [auditOrgFilter, setAuditOrgFilter] = useState('')
  const [auditSearchQuery, setAuditSearchQuery] = useState('')
  const [organisationsLoading, setOrganisationsLoading] = useState(false)
  const [orgModalOpen, setOrgModalOpen] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<Organisation | null>(null)
  const [orgDetailModalOpen, setOrgDetailModalOpen] = useState(false)
  const [viewingOrg, setViewingOrg] = useState<Organisation | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState('')
  const [workOrderPriorityFilter, setWorkOrderPriorityFilter] = useState('')
  const [workOrderTypeFilter, setWorkOrderTypeFilter] = useState('')
  const [workOrderAssetFilter, setWorkOrderAssetFilter] = useState('')
  const [workOrderAssigneeFilter, setWorkOrderAssigneeFilter] = useState('')
  const [workOrderSearchQuery, setWorkOrderSearchQuery] = useState('')

  // Show deleted filters (soft delete)
  const [showDeletedProducts, setShowDeletedProducts] = useState(initialRoute.showDeleted === 'true')
  const [showDeletedWorkOrders, setShowDeletedWorkOrders] = useState(false)
  const [showDeletedUsers, setShowDeletedUsers] = useState(false)
  const [showDeletedMasterProducts, setShowDeletedMasterProducts] = useState(false)

  // Modal states
  const [productDetailModalOpen, setProductDetailModalOpen] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [deleteProductModalOpen, setDeleteProductModalOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState<Product | null>(null)

  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null)
  const [workOrderDetailModalOpen, setWorkOrderDetailModalOpen] = useState(false)
  const [deleteWorkOrderModalOpen, setDeleteWorkOrderModalOpen] = useState(false)
  const [workOrderToDelete, setWorkOrderToDelete] = useState<WorkOrder | null>(null)

  const [userModalOpen, setUserModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false)
  const [userToResetPassword, setUserToResetPassword] = useState<User | null>(null)

  const [masterProductDetailModalOpen, setMasterProductDetailModalOpen] = useState(false)
  const [selectedMasterProduct, setSelectedMasterProduct] = useState<MasterProduct | null>(null)
  const [deleteMasterProductModalOpen, setDeleteMasterProductModalOpen] = useState(false)
  const [masterProductToDelete, setMasterProductToDelete] = useState<MasterProduct | null>(null)

  // IoT state
  const [iotDeviceDetailModalOpen, setIotDeviceDetailModalOpen] = useState(false)
  const [selectedIotDevice, setSelectedIotDevice] = useState<IoTDevice | null>(null)
  const [sensorReadings, setSensorReadings] = useState<any[]>([])
  const [sensorReadingsLoading, setSensorReadingsLoading] = useState(false)
  const [iotStatusFilter, setIotStatusFilter] = useState('')
  const [iotTypeFilter, setIotTypeFilter] = useState('')
  const [iotSearchQuery, setIotSearchQuery] = useState('')
  const [iotSortBy, setIotSortBy] = useState<'id' | 'deviceType' | 'status' | 'batteryLevel' | 'lastSeenAt'>('lastSeenAt')

  // Global smart search state
  const [globalSearch, setGlobalSearch] = useState('')
  const [globalSearchResults, setGlobalSearchResults] = useState<Array<{
    type: 'asset' | 'workorder' | 'user' | 'iot'
    id: string
    title: string
    subtitle: string
    location?: string
    data: any
  }>>([])
  const [showGlobalSearchResults, setShowGlobalSearchResults] = useState(false)
  const globalSearchRef = useRef<HTMLDivElement>(null)
  const [iotSortDir, setIotSortDir] = useState<'asc' | 'desc'>('desc')
  const [iotExporting, setIotExporting] = useState(false)
  const [showIotSavedReports, setShowIotSavedReports] = useState(false)
  const [iotSavedReports, setIotSavedReports] = useState<Array<{ key: string; size: number; lastModified: string; filename?: string }>>([])
  const [iotSavedReportsLoading, setIotSavedReportsLoading] = useState(false)

  // ESG filter/sort states
  const [esgSourceFilter, setEsgSourceFilter] = useState('')
  const [esgDateFrom, setEsgDateFrom] = useState('')
  const [esgDateTo, setEsgDateTo] = useState('')
  const [esgSortBy, setEsgSortBy] = useState<'periodStart' | 'carbonSaved' | 'energyGenerated' | 'wasteCollected' | 'recyclingRate'>('periodStart')
  const [esgSortDir, setEsgSortDir] = useState<'asc' | 'desc'>('desc')
  const [esgExporting, setEsgExporting] = useState(false)
  const [showSavedReports, setShowSavedReports] = useState(false)
  const [savedReports, setSavedReports] = useState<Array<{ key: string; size: number; lastModified: string; filename?: string }>>([])
  const [savedReportsLoading, setSavedReportsLoading] = useState(false)

  // Profile dropdown state
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false)
  const profileDropdownRef = useRef<HTMLDivElement>(null)

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false)
      }
    }
    if (profileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [profileDropdownOpen])

  // Alert/feedback states
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    title: string
    message: string
    type: 'info' | 'success' | 'error' | 'warning'
  }>({ isOpen: false, title: '', message: '', type: 'info' })

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Default headers for API calls
  // Map debug roles to seeded user emails
  const debugEmailMap: Record<string, string> = {
    'OrgAdmin': 'admin@esf.local',
    'AssetManager': 'manager@esf.local',
    'FieldTech': 'tech@esf.local',
    'Analyst': 'analyst@esf.local',
    'PublicViewer': 'viewer@esf.local',
  }

  const defaultHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    'x-user-role': debugRole,
    'x-user-email': debugEmailMap[debugRole] || `${debugRole.toLowerCase()}@esf.local`,
  }), [debugRole])

  const showAlert = (title: string, message: string, type: 'info' | 'success' | 'error' | 'warning' = 'info') => {
    setAlertModal({ isOpen: true, title, message, type })
  }

  // Global smart search handler
  const handleGlobalSearch = (query: string) => {
    setGlobalSearch(query)

    if (!query.trim()) {
      setGlobalSearchResults([])
      setShowGlobalSearchResults(false)
      return
    }

    const lowerQuery = query.toLowerCase()
    const results: typeof globalSearchResults = []

    // Search products (assets)
    products.forEach(product => {
      const locationStr = product.location?.address || product.location?.city || product.installationSite || ''
      if (
        product.name?.toLowerCase().includes(lowerQuery) ||
        product.id?.toLowerCase().includes(lowerQuery) ||
        product.serialNumber?.toLowerCase().includes(lowerQuery) ||
        product.passportId?.toLowerCase().includes(lowerQuery) ||
        locationStr.toLowerCase().includes(lowerQuery) ||
        product.category?.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          type: 'asset',
          id: product.id,
          title: product.name,
          subtitle: `${product.passportId || product.id} • ${product.category?.replace(/_/g, ' ')} • ${product.status}`,
          location: locationStr || undefined,
          data: product
        })
      }
    })

    // Search work orders
    workOrders.forEach(wo => {
      const assetName = products.find(p => p.id === wo.assetId)?.name || wo.assetId
      if (
        wo.title?.toLowerCase().includes(lowerQuery) ||
        wo.id?.toLowerCase().includes(lowerQuery) ||
        wo.description?.toLowerCase().includes(lowerQuery) ||
        assetName?.toLowerCase().includes(lowerQuery) ||
        wo.type?.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          type: 'workorder',
          id: wo.id,
          title: wo.title,
          subtitle: `${wo.type.replace(/_/g, ' ')} • ${wo.status} • ${wo.priority}`,
          location: assetName,
          data: wo
        })
      }
    })

    // Search users
    users.forEach(u => {
      if (
        u.name?.toLowerCase().includes(lowerQuery) ||
        u.email?.toLowerCase().includes(lowerQuery) ||
        u.role?.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          type: 'user',
          id: u.email, // User uses email as identifier
          title: u.name,
          subtitle: `${u.email} • ${u.role}`,
          data: u
        })
      }
    })

    // Search IoT devices
    iotDevices.forEach(device => {
      const assetName = products.find(p => p.id === device.assetId)?.name || device.assetId || ''
      if (
        device.id?.toLowerCase().includes(lowerQuery) ||
        device.deviceType?.toLowerCase().includes(lowerQuery) ||
        assetName.toLowerCase().includes(lowerQuery)
      ) {
        results.push({
          type: 'iot',
          id: device.id,
          title: device.id,
          subtitle: `${device.deviceType.replace(/_/g, ' ')} • ${device.status}`,
          location: assetName || undefined,
          data: device
        })
      }
    })

    // Search DDP templates (master products)
    masterProducts.forEach(mp => {
      if (
        mp.name?.toLowerCase().includes(lowerQuery) ||
        mp.id?.toLowerCase().includes(lowerQuery) ||
        mp.sku?.toLowerCase().includes(lowerQuery) ||
        mp.model?.toLowerCase().includes(lowerQuery) ||
        mp.category?.toLowerCase().includes(lowerQuery) ||
        mp.manufacturer?.toLowerCase().includes(lowerQuery)
      ) {
        const assetCount = products.filter(p => p.masterProductId === mp.id).length
        results.push({
          type: 'template',
          id: mp.id,
          title: mp.name,
          subtitle: `${mp.id} • ${mp.category?.replace(/_/g, ' ')} • v${mp.version}`,
          location: assetCount > 0 ? `${assetCount} asset${assetCount !== 1 ? 's' : ''}` : undefined,
          data: mp
        })
      }
    })

    setGlobalSearchResults(results.slice(0, 12)) // Limit to 12 results
    setShowGlobalSearchResults(true)
  }

  // Handle clicking a search result
  const handleSearchResultClick = (result: typeof globalSearchResults[0]) => {
    setShowGlobalSearchResults(false)
    setGlobalSearch('')

    if (result.type === 'asset') {
      navigateTo('assets')
      setSelectedProduct(result.data)
      setProductDetailModalOpen(true)
    } else if (result.type === 'workorder') {
      navigateTo('workorders')
      setSelectedWorkOrder(result.data)
      setWorkOrderDetailModalOpen(true)
    } else if (result.type === 'user') {
      navigateTo('users')
      setSelectedUser(result.data)
      setUserModalOpen(true)
    } else if (result.type === 'iot') {
      navigateTo('iot')
      setSelectedIotDevice(result.data)
      setIotDeviceDetailModalOpen(true)
    } else if (result.type === 'template') {
      navigateTo('dpp-templates')
      setSelectedMasterProduct(result.data)
      setMasterProductDetailModalOpen(true)
    }
  }

  // Close global search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (globalSearchRef.current && !globalSearchRef.current.contains(event.target as Node)) {
        setShowGlobalSearchResults(false)
      }
    }
    if (showGlobalSearchResults) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showGlobalSearchResults])

  // Check auth session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${base}/authc/whoami`, { credentials: 'include' })
        if (res.ok) {
          const data = await res.json()
          setUser({ email: data.email, role: data.role, organisationId: 'org-default' })
          setIsAuthenticated(true)
        } else {
          setUser(null)
          setIsAuthenticated(false)
        }
      } catch {
        setUser(null)
        setIsAuthenticated(false)
      } finally {
        setAuthChecking(false)
      }
    }
    checkAuth()
  }, [base])

  // Handle login success
  const handleLoginSuccess = (authInfo: { email: string; role: string | null; mode?: string }) => {
    setUser({ email: authInfo.email, role: authInfo.role as UserRole | null, organisationId: 'org-default' })
    setIsAuthenticated(true)
  }

  // Handle logout
  const handleLogout = async () => {
    try {
      await fetch(`${base}/authc/logout`, { method: 'POST', credentials: 'include' })
    } catch {
      // Ignore logout errors
    }
    setUser(null)
    setIsAuthenticated(false)
    // Force reload to ensure clean state
    window.location.reload()
  }

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.set('status', statusFilter)
      if (categoryFilter) params.set('category', categoryFilter)
      if (showDeletedProducts) params.set('includeDeleted', 'true')

      const res = await fetch(`${base}/assets?${params}`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setProducts(data.items || [])
      setError(null)
    } catch {
      setError('Failed to fetch products')
    } finally {
      setLoading(false)
    }
  }, [base, defaultHeaders, statusFilter, categoryFilter, showDeletedProducts])

  // Fetch all products (unfiltered) for Dashboard
  const fetchAllProducts = useCallback(async () => {
    try {
      const res = await fetch(`${base}/assets`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setAllProducts(data.items || [])
    } catch {
      // Silently fail - Dashboard will show empty if this fails
    }
  }, [base, defaultHeaders])

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${base}/assets/analytics`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setAnalytics(data)
    } catch {
      // Silently fail for analytics
    }
  }, [base, defaultHeaders])

  // Fetch work orders
  const fetchWorkOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (workOrderStatusFilter) params.set('status', workOrderStatusFilter)
      if (workOrderPriorityFilter) params.set('priority', workOrderPriorityFilter)
      if (showDeletedWorkOrders) params.set('includeDeleted', 'true')

      const res = await fetch(`${base}/workorders?${params}`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setWorkOrders(data.items || [])
    } catch {
      // Use mock data if API not available
      setWorkOrders([])
    }
  }, [base, defaultHeaders, workOrderStatusFilter, workOrderPriorityFilter, showDeletedWorkOrders])

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (showDeletedUsers) params.set('includeDeleted', 'true')

      const res = await fetch(`${base}/users?${params}`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setUsers(data.items || [])
    } catch {
      setUsers([])
    }
  }, [base, defaultHeaders, showDeletedUsers])

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (append = false) => {
    try {
      setAuditLogsLoading(true)
      const params = new URLSearchParams()
      if (auditActionFilter) params.set('action', auditActionFilter)
      if (auditActorFilter) params.set('actor', auditActorFilter)
      if (auditFromDate) params.set('fromDate', new Date(auditFromDate).toISOString())
      if (auditToDate) params.set('toDate', new Date(auditToDate + 'T23:59:59').toISOString())
      if (auditSearchQuery) params.set('search', auditSearchQuery)
      if (auditOrgFilter) params.set('organisationId', auditOrgFilter)
      if (append && auditNextKey) params.set('lastKey', auditNextKey)
      params.set('limit', '50')

      const res = await fetch(`${base}/admin/audit-logs?${params}`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      if (append) {
        setAuditLogs(prev => [...prev, ...(data.items || [])])
      } else {
        setAuditLogs(data.items || [])
      }
      setAuditNextKey(data.nextKey || null)
    } catch {
      if (!append) setAuditLogs([])
    } finally {
      setAuditLogsLoading(false)
    }
  }, [base, defaultHeaders, auditActionFilter, auditActorFilter, auditFromDate, auditToDate, auditSearchQuery, auditOrgFilter, auditNextKey])

  // Fetch IoT devices
  const fetchIotDevices = useCallback(async () => {
    try {
      const res = await fetch(`${base}/iot/devices`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setIotDevices(data.items || [])
    } catch {
      setIotDevices([])
    }
  }, [base, defaultHeaders])

  // Fetch ESG metrics
  const fetchEsgMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${base}/esg/metrics`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setEsgMetrics(data.items || [])
    } catch {
      setEsgMetrics([])
    }
  }, [base, defaultHeaders])

  // Fetch sensor readings for a device
  const fetchSensorReadings = useCallback(async (deviceId: string) => {
    setSensorReadingsLoading(true)
    try {
      const res = await fetch(`${base}/iot/devices/${deviceId}/readings?limit=50`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setSensorReadings(data.items || [])
    } catch {
      setSensorReadings([])
    } finally {
      setSensorReadingsLoading(false)
    }
  }, [base, defaultHeaders])

  // Fetch master products
  const fetchMasterProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (showDeletedMasterProducts) params.set('includeDeleted', 'true')

      const res = await fetch(`${base}/master-products?${params}`, { headers: defaultHeaders, credentials: 'include' })
      const data = await res.json()
      setMasterProducts(data || [])
    } catch {
      setMasterProducts([])
    }
  }, [base, defaultHeaders, showDeletedMasterProducts])

  // Fetch organisations (SuperAdmin only)
  const fetchOrganisations = useCallback(async () => {
    setOrganisationsLoading(true)
    try {
      const res = await fetch(`${base}/organisations`, { headers: defaultHeaders, credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        const orgs = data.items || []
        setOrganisations(orgs)
        // Fetch stats for each organisation
        const statsMap: typeof orgStats = {}
        for (const org of orgs) {
          try {
            const statsRes = await fetch(`${base}/organisations/${org.id}/stats`, { headers: defaultHeaders, credentials: 'include' })
            if (statsRes.ok) {
              const stats = await statsRes.json()
              statsMap[org.id] = {
                organisationId: org.id,
                assetCount: stats.assetCount || 0,
                userCount: stats.userCount || 0,
                workOrderCount: stats.workOrderCount || 0,
                iotDeviceCount: stats.iotDeviceCount || 0,
              }
            }
          } catch {
            // Stats fetch failed, continue without stats
          }
        }
        setOrgStats(statsMap)
      }
    } catch {
      setOrganisations([])
    } finally {
      setOrganisationsLoading(false)
    }
  }, [base, defaultHeaders])

  // Fetch admin dashboard stats (SuperAdmin only)
  const fetchAdminDashboard = useCallback(async (orgId?: string | null) => {
    setAdminDashboardLoading(true)
    try {
      const url = orgId ? `${base}/admin/dashboard?organisationId=${orgId}` : `${base}/admin/dashboard`
      const res = await fetch(url, { headers: defaultHeaders, credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setAdminDashboardStats(data)
      }
    } catch {
      setAdminDashboardStats(null)
    } finally {
      setAdminDashboardLoading(false)
    }
  }, [base, defaultHeaders])

  useEffect(() => {
    fetchProducts()
    fetchAllProducts()
    fetchAnalytics()
    fetchMasterProducts()
  }, [fetchProducts, fetchAllProducts, fetchAnalytics, fetchMasterProducts])

  const isSuperAdmin = (user?.role || debugRole) === 'SuperAdmin'

  useEffect(() => {
    if (currentPage === 'workorders') fetchWorkOrders()
    if (currentPage === 'users') {
      fetchUsers()
      // SuperAdmin needs organisations for the org filter dropdown
      if (isSuperAdmin) fetchOrganisations()
    }
    if (currentPage === 'iot') {
      fetchIotDevices()
      fetchProducts() // Also fetch products to resolve asset names
    }
    if (currentPage === 'esg') fetchEsgMetrics()
    if (currentPage === 'dpp-templates') fetchMasterProducts()
    if (currentPage === 'product-create' || currentPage === 'product-edit') fetchMasterProducts()
    // SuperAdmin pages
    if (currentPage === 'admin-dashboard') {
      fetchAdminDashboard(selectedAdminOrgId)
      fetchOrganisations()
    }
    if (currentPage === 'organisations') fetchOrganisations()
    if (currentPage === 'audit-logs') {
      fetchAuditLogs()
      if (isSuperAdmin) fetchOrganisations()
    }
  }, [currentPage, fetchWorkOrders, fetchUsers, fetchIotDevices, fetchEsgMetrics, fetchMasterProducts, fetchProducts, fetchAdminDashboard, fetchOrganisations, fetchAuditLogs, selectedAdminOrgId, isSuperAdmin])

  // Sync URL with modal states (update URL when modals open/close)
  useEffect(() => {
    if (productDetailModalOpen && selectedProduct) {
      setUrlView(`product-${selectedProduct.id}`)
    } else if (workOrderDetailModalOpen && selectedWorkOrder) {
      setUrlView(`workorder-${selectedWorkOrder.id}`)
    } else if (orgDetailModalOpen && viewingOrg) {
      setUrlView(`org-${viewingOrg.id}`)
    } else if (iotDeviceDetailModalOpen && selectedIotDevice) {
      setUrlView(`iot-${selectedIotDevice.id}`)
    } else if (masterProductDetailModalOpen && selectedMasterProduct) {
      setUrlView(`template-${selectedMasterProduct.id}`)
    } else {
      setUrlView(null)
    }
  }, [productDetailModalOpen, selectedProduct, workOrderDetailModalOpen, selectedWorkOrder, orgDetailModalOpen, viewingOrg, iotDeviceDetailModalOpen, selectedIotDevice, masterProductDetailModalOpen, selectedMasterProduct, setUrlView])

  // Handle deep link to view items from URL (restore modal state from URL)
  useEffect(() => {
    const route = getRouteFromHash()
    if (!route.view) return

    // Parse view parameter: type-id format
    const [viewType, ...idParts] = route.view.split('-')
    const viewId = idParts.join('-')
    if (!viewId) return

    // Open appropriate modal based on view type
    if (viewType === 'product' && products.length > 0) {
      const product = products.find(p => p.id === viewId)
      if (product && !productDetailModalOpen) {
        setSelectedProduct(product)
        setProductDetailModalOpen(true)
      }
    } else if (viewType === 'workorder' && workOrders.length > 0) {
      const wo = workOrders.find(w => w.id === viewId)
      if (wo && !workOrderDetailModalOpen) {
        setSelectedWorkOrder(wo)
        setWorkOrderDetailModalOpen(true)
      }
    } else if (viewType === 'org' && organisations.length > 0) {
      const org = organisations.find(o => o.id === viewId)
      if (org && !orgDetailModalOpen) {
        setViewingOrg(org)
        setOrgDetailModalOpen(true)
      }
    } else if (viewType === 'iot' && iotDevices.length > 0) {
      const device = iotDevices.find(d => d.id === viewId)
      if (device && !iotDeviceDetailModalOpen) {
        setSelectedIotDevice(device)
        setIotDeviceDetailModalOpen(true)
      }
    } else if (viewType === 'template' && masterProducts.length > 0) {
      const template = masterProducts.find(t => t.id === viewId)
      if (template && !masterProductDetailModalOpen) {
        setSelectedMasterProduct(template)
        setMasterProductDetailModalOpen(true)
      }
    }
  }, [products, workOrders, organisations, iotDevices, masterProducts, productDetailModalOpen, workOrderDetailModalOpen, orgDetailModalOpen, iotDeviceDetailModalOpen, masterProductDetailModalOpen])

  // Handle deep link to work order (from notification)
  useEffect(() => {
    // Only run after authentication is confirmed (or in debug mode)
    if (viewWorkOrderId && currentPage === 'workorders' && (isAuthenticated || debugMode)) {
      // Fetch the specific work order and select it
      const fetchAndSelectWorkOrder = async () => {
        try {
          const res = await fetch(`${base}/workorders/${viewWorkOrderId}`, {
            headers: defaultHeaders,
            credentials: 'include',
          })
          if (res.ok) {
            const wo = await res.json()
            setSelectedWorkOrder(wo)
            setWorkOrderDetailModalOpen(true)
          }
        } catch (e) {
          console.error('Failed to fetch work order:', e)
        } finally {
          // Clear the view ID and update URL to just workorders
          setViewWorkOrderId(null)
          clearRouteParams(['view'], true)
        }
      }
      fetchAndSelectWorkOrder()
    }
  }, [viewWorkOrderId, currentPage, base, defaultHeaders, isAuthenticated, debugMode])

  // Filter products by search query
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products
    const q = searchQuery.toLowerCase()
    return products.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      p.passportId?.toLowerCase().includes(q) ||
      p.id?.toLowerCase().includes(q) ||
      p.serialNumber?.toLowerCase().includes(q) ||
      p.model?.toLowerCase().includes(q)
    )
  }, [products, searchQuery])

  // Product delete operation
  const handleDeleteProduct = async () => {
    if (!productToDelete) return
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/assets/${productToDelete.id}`, {
        method: 'DELETE',
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete product')
      setDeleteProductModalOpen(false)
      setProductToDelete(null)
      await fetchProducts()
      await fetchAllProducts()
      await fetchAnalytics()
      showAlert('Success', 'Product moved to deleted items', 'success')
    } catch {
      showAlert('Error', 'Failed to delete product. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Restore product (soft delete recovery)
  const handleRestoreProduct = async (product: Product) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/assets/${product.id}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ deletedAt: null }),
      })
      if (!res.ok) throw new Error('Failed to restore product')
      await fetchProducts()
      await fetchAllProducts()
      await fetchAnalytics()
      showAlert('Success', 'Product restored successfully', 'success')
    } catch {
      showAlert('Error', 'Failed to restore product. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Work Order operations
  const handleDeleteWorkOrder = async () => {
    if (!workOrderToDelete) return
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/workorders/${workOrderToDelete.id}`, {
        method: 'DELETE',
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete work order')
      setDeleteWorkOrderModalOpen(false)
      setWorkOrderToDelete(null)
      await fetchWorkOrders()
      showAlert('Success', 'Work order moved to deleted items', 'success')
    } catch {
      showAlert('Error', 'Failed to delete work order. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Restore work order (soft delete recovery)
  const handleRestoreWorkOrder = async (workOrder: WorkOrder) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/workorders/${workOrder.id}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ deletedAt: null }),
      })
      if (!res.ok) throw new Error('Failed to restore work order')
      await fetchWorkOrders()
      showAlert('Success', 'Work order restored successfully', 'success')
    } catch {
      showAlert('Error', 'Failed to restore work order. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateWorkOrderStatus = async (workOrder: WorkOrder, newStatus: string) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/workorders/${workOrder.id}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      })
      if (!res.ok) throw new Error('Failed to update status')
      await fetchWorkOrders()
      showAlert('Success', `Work order status updated to ${newStatus.replace(/_/g, ' ')}`, 'success')
    } catch {
      showAlert('Error', 'Failed to update work order status.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // User CRUD operations
  const handleCreateUser = async (data: UserInput) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/users`, {
        method: 'POST',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to create user')
      const result = await res.json()
      setUserModalOpen(false)
      setSelectedUser(null)
      await fetchUsers()

      // Show different message based on mode (mock vs cognito)
      if (result.tempPassword) {
        // Mock/dev mode - show the temp password
        showAlert(
          'User Created - DEV MODE',
          `User ${data.email} created successfully.\n\nTemporary password: ${result.tempPassword}\n\nShare this password with the user. They will be required to change it on first login.`,
          'info'
        )
      } else {
        // Production mode - password was emailed
        showAlert('Success', 'User created successfully. A temporary password has been sent to their email.', 'success')
      }
    } catch {
      showAlert('Error', 'Failed to create user. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateUser = async (data: UserInput) => {
    if (!selectedUser) return
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/users/${selectedUser.email}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) throw new Error('Failed to update user')
      setUserModalOpen(false)
      setSelectedUser(null)
      await fetchUsers()
      showAlert('Success', 'User updated successfully', 'success')
    } catch {
      showAlert('Error', 'Failed to update user. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteUser = async () => {
    if (!userToDelete) return
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/users/${userToDelete.email}`, {
        method: 'DELETE',
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete user')
      setDeleteUserModalOpen(false)
      setUserToDelete(null)
      await fetchUsers()
      showAlert('Success', 'User moved to deleted items', 'success')
    } catch {
      showAlert('Error', 'Failed to delete user. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Restore user (soft delete recovery)
  const handleRestoreUser = async (user: User) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/users/${user.email}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ deletedAt: null }),
      })
      if (!res.ok) throw new Error('Failed to restore user')
      await fetchUsers()
      showAlert('Success', 'User restored successfully', 'success')
    } catch {
      showAlert('Error', 'Failed to restore user. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleUserEnabled = async (userToToggle: User) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/users/${userToToggle.email}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ enabled: !userToToggle.enabled }),
      })
      if (!res.ok) throw new Error('Failed to update user')
      await fetchUsers()
      showAlert('Success', `User ${userToToggle.enabled ? 'disabled' : 'enabled'} successfully`, 'success')
    } catch {
      showAlert('Error', 'Failed to update user status.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePasswordReset = (userToReset: User) => {
    setUserToResetPassword(userToReset)
    setResetPasswordModalOpen(true)
  }

  const confirmPasswordReset = async () => {
    if (!userToResetPassword) return

    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/authc/password-reset`, {
        method: 'POST',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ email: userToResetPassword.email }),
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Failed to reset password')

      if (result.tempPassword) {
        // Mock/dev mode - show the temp password
        showAlert(
          'Password Reset - DEV MODE',
          `Temporary password generated for ${userToResetPassword.email}:\n\n${result.tempPassword}\n\nShare this password with the user. They will be required to change it on first login.\n\nNote: In production, this password is sent via email automatically.`,
          'info'
        )
      } else {
        // Production mode - password was emailed
        showAlert('Success', result.message || 'Password reset email sent successfully.', 'success')
      }
    } catch (e) {
      showAlert('Error', e instanceof Error ? e.message : 'Failed to reset password.', 'error')
    } finally {
      setIsSubmitting(false)
      setResetPasswordModalOpen(false)
      setUserToResetPassword(null)
    }
  }

  // Organisation CRUD operations
  const handleCreateOrganisation = async (data: OrganisationInput) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/organisations`, {
        method: 'POST',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || 'Failed to create organisation')
      }
      setOrgModalOpen(false)
      setSelectedOrg(null)
      await fetchOrganisations()
      showAlert('Success', `Organisation "${data.name}" created successfully`, 'success')
    } catch (e) {
      showAlert('Error', e instanceof Error ? e.message : 'Failed to create organisation', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateOrganisation = async (data: OrganisationInput) => {
    if (!selectedOrg) return
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/organisations/${selectedOrg.id}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || 'Failed to update organisation')
      }
      setOrgModalOpen(false)
      setSelectedOrg(null)
      await fetchOrganisations()
      showAlert('Success', 'Organisation updated successfully', 'success')
    } catch (e) {
      showAlert('Error', e instanceof Error ? e.message : 'Failed to update organisation', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleOrgEnabled = async (org: Organisation) => {
    try {
      const res = await fetch(`${base}/organisations/${org.id}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ enabled: org.enabled === false }),
      })
      if (res.ok) {
        fetchOrganisations()
        showAlert('Success', `Organisation ${org.enabled !== false ? 'disabled' : 'enabled'} successfully`, 'success')
      } else {
        const data = await res.json()
        showAlert('Error', data.error || 'Failed to update organisation', 'error')
      }
    } catch {
      showAlert('Error', 'Failed to update organisation', 'error')
    }
  }

  // Master Product CRUD operations
  const handleDeleteMasterProduct = async () => {
    if (!masterProductToDelete) return
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/master-products/${masterProductToDelete.id}`, {
        method: 'DELETE',
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete DPP template')
      setDeleteMasterProductModalOpen(false)
      setMasterProductToDelete(null)
      await fetchMasterProducts()
      showAlert('Success', 'DPP template moved to deleted items', 'success')
    } catch {
      showAlert('Error', 'Failed to delete DPP template. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Restore master product (soft delete recovery)
  const handleRestoreMasterProduct = async (masterProduct: MasterProduct) => {
    setIsSubmitting(true)
    try {
      const res = await fetch(`${base}/master-products/${masterProduct.id}`, {
        method: 'PATCH',
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify({ deletedAt: null }),
      })
      if (!res.ok) throw new Error('Failed to restore DPP template')
      await fetchMasterProducts()
      showAlert('Success', 'DPP template restored successfully', 'success')
    } catch {
      showAlert('Error', 'Failed to restore DPP template. Please try again.', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Navigation items with role-based access control
  // roles: which roles can see this nav item (undefined = all roles)
  const allNavItems: Array<{
    id: Page
    label: string
    icon: string
    section?: string
    roles?: UserRole[]
  }> = [
    // SuperAdmin-only platform pages
    { id: 'admin-dashboard', label: 'Platform Overview', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z', section: 'Platform', roles: ['SuperAdmin'] },
    { id: 'organisations', label: 'Organisations', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', section: 'Platform', roles: ['SuperAdmin'] },
    { id: 'audit-logs', label: 'Audit Logs', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01', section: 'Platform', roles: ['SuperAdmin', 'OrgAdmin'] },
    // Regular pages
    { id: 'dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { id: 'assets', label: 'Product Registry', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech', 'Analyst'] },
    { id: 'dpp-templates', label: 'DPP Templates', icon: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z', roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager'] },
    { id: 'iot', label: 'IoT Dashboard', icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z', roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech', 'Analyst'] },
    { id: 'esg', label: 'ESG Reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'Analyst'] },
    { id: 'workorders', label: 'Work Orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', roles: ['SuperAdmin', 'OrgAdmin', 'AssetManager', 'FieldTech'] },
    { id: 'users', label: 'Users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', section: 'Admin', roles: ['SuperAdmin', 'OrgAdmin'] },
    { id: 'settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', roles: ['SuperAdmin', 'OrgAdmin'] },
    { id: 'tutorial', label: 'Tutorial', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', section: 'Help' },
  ]

  // Filter navigation items based on current user role
  const navItems = useMemo(() => {
    const userRole = user?.role || debugRole
    return allNavItems.filter(item => !item.roles || item.roles.includes(userRole as UserRole))
  }, [user?.role, debugRole])

  // Render icon SVG
  const renderIcon = (path: string) => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
    </svg>
  )

  // Category badge component
  const CategoryBadge = ({ category }: { category: string }) => {
    return (
      <span className="badge bg-esf-navy/10 text-esf-navy">
        {ProductCategoryLabels[category as keyof typeof ProductCategoryLabels] || category}
      </span>
    )
  }

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      ACTIVE: 'badge-status-active',
      MAINTENANCE: 'badge-status-maintenance',
      DECOMMISSIONED: 'badge-status-decommissioned',
      PENDING_INSTALLATION: 'badge-status-pending_installation',
      REPAIR_NEEDED: 'badge-status-repair_needed',
    }
    return <span className={`badge ${colors[status] || 'bg-gray-100 text-gray-700'}`}>{status.replace(/_/g, ' ')}</span>
  }

  // Priority badge component
  const PriorityBadge = ({ priority }: { priority: string }) => {
    const colors: Record<string, string> = {
      LOW: 'bg-gray-100 text-gray-700',
      MEDIUM: 'bg-info-100 text-info-700',
      HIGH: 'bg-warning-100 text-warning-700',
      URGENT: 'bg-error-100 text-error-700',
    }
    return <span className={`badge ${colors[priority] || 'bg-gray-100 text-gray-700'}`}>{priority}</span>
  }

  // Work order status badge
  const WorkOrderStatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-gray-100 text-gray-700',
      SCHEDULED: 'bg-info-100 text-info-700',
      IN_PROGRESS: 'bg-warning-100 text-warning-700',
      COMPLETED: 'bg-success-100 text-success-700',
      CANCELLED: 'bg-gray-100 text-gray-500',
      ON_HOLD: 'bg-purple-100 text-purple-700',
    }
    return <span className={`badge ${colors[status] || 'bg-gray-100 text-gray-700'}`}>{status.replace(/_/g, ' ')}</span>
  }

  // IoT status badge
  const IoTStatusBadge = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      ONLINE: 'bg-success-100 text-success-700',
      OFFLINE: 'bg-gray-100 text-gray-500',
      MAINTENANCE: 'bg-warning-100 text-warning-700',
      ERROR: 'bg-error-100 text-error-700',
    }
    return <span className={`badge ${colors[status] || 'bg-gray-100 text-gray-700'}`}>{status}</span>
  }

  // KPI Card component
  const KpiCard = ({ title, value, change, icon, color }: { title: string; value: string | number; change?: string; icon: string; color: string }) => (
    <div className="kpi">
      <div className={`kpi-icon ${color}`}>
        {renderIcon(icon)}
      </div>
      <div className="kpi-title">{title}</div>
      <div className="kpi-value">{value}</div>
      {change && <div className={`kpi-change ${change.startsWith('+') ? 'positive' : 'negative'}`}>{change}</div>}
    </div>
  )

  // Dashboard Page
  // Dashboard - DEPRECATED: Now using imported DashboardPage component
  const _OldDashboardPage = () => (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <button onClick={() => { fetchProducts(); fetchAnalytics() }} className="btn btn-secondary">
          {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KpiCard
          title="Total Products"
          value={analytics?.total || 0}
          icon="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          color="bg-esf-navy/10 text-esf-navy"
        />
        <KpiCard
          title="Active Products"
          value={analytics?.byStatus?.ACTIVE || 0}
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          color="bg-success-100 text-success-600"
        />
        <KpiCard
          title="Maintenance Required"
          value={(analytics?.byStatus?.MAINTENANCE || 0) + (analytics?.byStatus?.REPAIR_NEEDED || 0)}
          icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          color="bg-warning-100 text-warning-600"
        />
        <KpiCard
          title="Carbon Footprint"
          value={`${((analytics?.totalCarbonFootprint || 0) / 1000).toFixed(1)}t`}
          icon="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          color="bg-info-100 text-info-600"
        />
      </div>

      {/* Products by Category */}
      <div className="card mb-8">
        <div className="card-header">
          <h2 className="card-title">Products by Category</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {ProductCategories.filter(cat => (analytics?.byCategory?.[cat] || 0) > 0 || cat === 'seating' || cat === 'litter_recycling' || cat === 'shelters' || cat === 'bollards_barriers').slice(0, 5).map(cat => (
              <div key={cat} className="text-center p-4 rounded-lg bg-gray-50">
                <CategoryBadge category={cat} />
                <div className="text-2xl font-bold mt-2">{analytics?.byCategory?.[cat] || 0}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Products */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="card-title">Recent Products</h2>
          <button onClick={() => navigateTo('assets')} className="btn btn-sm btn-outline">View All</button>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Passport ID</th>
                <th>Category</th>
                <th>Status</th>
                <th>Subcategory</th>
              </tr>
            </thead>
            <tbody>
              {products.slice(0, 5).map(product => (
                <tr
                  key={product.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    setSelectedProduct(product)
                    setProductDetailModalOpen(true)
                  }}
                >
                  <td className="font-medium">{product.name}</td>
                  <td className="font-mono text-sm text-gray-500">{product.passportId}</td>
                  <td><CategoryBadge category={product.category} /></td>
                  <td><StatusBadge status={product.status} /></td>
                  <td className="text-sm text-gray-600">{product.subcategory || '-'}</td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">No products found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  // Products Page - DEPRECATED: Now using imported ProductsPage component
  const _OldProductsPage = () => (
    <div className="container-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Product Registry</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigateTo('product-create')}
        >
          {renderIcon('M12 4v16m8-8H4')}
          Add Product
        </button>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search products..."
          className="filter-input flex-1"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <select
          className="filter-select"
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {ProductCategories.map(c => <option key={c} value={c}>{ProductCategoryLabels[c]}</option>)}
        </select>
        <select
          className="filter-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All Statuses</option>
          {ProductStatuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <button onClick={fetchProducts} className="btn btn-secondary">
          {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
        </button>
      </div>

      {/* Product Table */}
      <div className="table-card">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-esf-navy"></div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Passport ID</th>
                <th>Category</th>
                <th>Subcategory</th>
                <th>Status</th>
                <th>Condition</th>
                <th>Location</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map(product => (
                <tr key={product.id}>
                  <td className="font-medium">{product.name}</td>
                  <td className="font-mono text-sm text-gray-500">{product.passportId}</td>
                  <td><CategoryBadge category={product.category} /></td>
                  <td className="text-sm text-gray-600">{product.subcategory || '-'}</td>
                  <td><StatusBadge status={product.status} /></td>
                  <td>
                    <span className={`badge badge-condition-${(product.condition || 'good').toLowerCase()}`}>
                      {product.condition || 'Good'}
                    </span>
                  </td>
                  <td className="text-sm text-gray-500">
                    {product.location ? `${product.location.lat?.toFixed(4)}, ${product.location.lng?.toFixed(4)}` : '-'}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setSelectedProduct(product)
                          setProductDetailModalOpen(true)
                        }}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigateTo('product-edit', product.id)}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        {renderIcon('M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10')}
                      </div>
                      <div className="empty-state-title">No products found</div>
                      <div className="empty-state-text">
                        {searchQuery || statusFilter || categoryFilter
                          ? 'Try adjusting your filters'
                          : 'Get started by adding your first product'}
                      </div>
                      {!searchQuery && !statusFilter && !categoryFilter && (
                        <button
                          className="btn btn-primary mt-4"
                          onClick={() => navigateTo('product-create')}
                        >
                          {renderIcon('M12 4v16m8-8H4')}
                          Add Product
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-error-100 text-error-600 rounded-lg">{error}</div>
      )}
    </div>
  )

  // Work Orders Page - DEPRECATED: Now using imported WorkOrdersPage component
  const _OldWorkOrdersPage = () => {
    const workOrderTypeLabels: Record<string, string> = {
      INSPECTION: 'Inspection',
      MAINTENANCE: 'Scheduled Maintenance',
      REPAIR: 'Repair',
      INSTALLATION: 'Installation',
      DECOMMISSION: 'Decommission',
      SENSOR_REPLACEMENT: 'Sensor Replacement',
    }

    const formatDate = (dateStr?: string) => {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }

    const getProductName = (assetId: string) => {
      const product = products.find(p => p.id === assetId)
      return product?.name || assetId
    }

    // Get unique assignees from work orders
    const uniqueAssignees = [...new Set(workOrders.map(wo => wo.assignedTo).filter(Boolean))] as string[]

    // Get unique assets that have work orders
    const assetsWithWorkOrders = [...new Set(workOrders.map(wo => wo.assetId))]
    const assetOptions = products.filter(p => assetsWithWorkOrders.includes(p.id))

    // Apply client-side filters
    const filteredWorkOrders = workOrders.filter(wo => {
      if (workOrderTypeFilter && wo.type !== workOrderTypeFilter) return false
      if (workOrderAssetFilter && wo.assetId !== workOrderAssetFilter) return false
      if (workOrderAssigneeFilter && wo.assignedTo !== workOrderAssigneeFilter) return false
      if (workOrderSearchQuery) {
        const query = workOrderSearchQuery.toLowerCase()
        const matchesTitle = wo.title.toLowerCase().includes(query)
        const matchesDescription = wo.description?.toLowerCase().includes(query)
        const matchesAsset = getProductName(wo.assetId).toLowerCase().includes(query)
        if (!matchesTitle && !matchesDescription && !matchesAsset) return false
      }
      return true
    })

    // Calculate statistics
    const selectedAssetWorkOrderCount = workOrderAssetFilter
      ? workOrders.filter(wo => wo.assetId === workOrderAssetFilter).length
      : null
    const selectedAssigneeWorkOrderCount = workOrderAssigneeFilter
      ? workOrders.filter(wo => wo.assignedTo === workOrderAssigneeFilter).length
      : null

    const hasActiveFilters = workOrderStatusFilter || workOrderPriorityFilter || workOrderTypeFilter ||
      workOrderAssetFilter || workOrderAssigneeFilter || workOrderSearchQuery

    const clearAllFilters = () => {
      setWorkOrderStatusFilter('')
      setWorkOrderPriorityFilter('')
      setWorkOrderTypeFilter('')
      setWorkOrderAssetFilter('')
      setWorkOrderAssigneeFilter('')
      setWorkOrderSearchQuery('')
    }

    return (
      <div className="container-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Work Orders</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigateTo('workorder-create')}
          >
            {renderIcon('M12 4v16m8-8H4')}
            Create Work Order
          </button>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search work orders by title, description, or asset..."
              value={workOrderSearchQuery}
              onChange={e => setWorkOrderSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-esf-navy/20 focus:border-esf-navy"
            />
            {workOrderSearchQuery && (
              <button
                onClick={() => setWorkOrderSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="filter-bar flex-wrap gap-2">
          <select
            className="filter-select"
            value={workOrderStatusFilter}
            onChange={e => setWorkOrderStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {WorkOrderStatuses.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <select
            className="filter-select"
            value={workOrderPriorityFilter}
            onChange={e => setWorkOrderPriorityFilter(e.target.value)}
          >
            <option value="">All Priorities</option>
            {Priorities.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            className="filter-select"
            value={workOrderTypeFilter}
            onChange={e => setWorkOrderTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {WorkOrderTypes.map(t => <option key={t} value={t}>{workOrderTypeLabels[t] || t}</option>)}
          </select>
          <select
            className="filter-select"
            value={workOrderAssetFilter}
            onChange={e => setWorkOrderAssetFilter(e.target.value)}
          >
            <option value="">All Assets</option>
            {assetOptions.map(p => (
              <option key={p.id} value={p.id}>
                {p.name} ({workOrders.filter(wo => wo.assetId === p.id).length})
              </option>
            ))}
          </select>
          <select
            className="filter-select"
            value={workOrderAssigneeFilter}
            onChange={e => setWorkOrderAssigneeFilter(e.target.value)}
          >
            <option value="">All Assignees</option>
            {uniqueAssignees.map(assignee => (
              <option key={assignee} value={assignee}>
                {assignee} ({workOrders.filter(wo => wo.assignedTo === assignee).length})
              </option>
            ))}
          </select>
          <button onClick={fetchWorkOrders} className="btn btn-secondary" title="Refresh">
            {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
          </button>
          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="btn btn-outline text-sm">
              Clear filters
            </button>
          )}
        </div>

        {/* Statistics Banner */}
        {(selectedAssetWorkOrderCount !== null || selectedAssigneeWorkOrderCount !== null) && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex flex-wrap gap-6">
              {selectedAssetWorkOrderCount !== null && (
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <span className="text-blue-800">
                    <strong>{getProductName(workOrderAssetFilter)}</strong> has{' '}
                    <strong>{selectedAssetWorkOrderCount}</strong> total work order{selectedAssetWorkOrderCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {selectedAssigneeWorkOrderCount !== null && (
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="text-blue-800">
                    <strong>{workOrderAssigneeFilter}</strong> has been assigned{' '}
                    <strong>{selectedAssigneeWorkOrderCount}</strong> work order{selectedAssigneeWorkOrderCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Results count */}
        {hasActiveFilters && (
          <div className="mt-4 text-sm text-gray-500">
            Showing {filteredWorkOrders.length} of {workOrders.length} work orders
          </div>
        )}

        {/* Work Orders Table */}
        <div className="table-card">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Asset</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Scheduled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredWorkOrders.map(wo => (
                <tr key={wo.id}>
                  <td className="font-medium">{wo.title}</td>
                  <td className="text-sm text-gray-600">{getProductName(wo.assetId)}</td>
                  <td className="text-sm">{workOrderTypeLabels[wo.type] || wo.type}</td>
                  <td><PriorityBadge priority={wo.priority} /></td>
                  <td><WorkOrderStatusBadge status={wo.status} /></td>
                  <td className="text-sm text-gray-500">{wo.assignedTo || '-'}</td>
                  <td className="text-sm text-gray-500">{formatDate(wo.scheduledDate)}</td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setSelectedWorkOrder(wo)
                          setWorkOrderDetailModalOpen(true)
                        }}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigateTo('workorder-edit', wo.id)}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredWorkOrders.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state-icon bg-warning-100 text-warning-600">
                        {renderIcon('M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4')}
                      </div>
                      <div className="empty-state-title">No work orders found</div>
                      <div className="empty-state-text">
                        {hasActiveFilters
                          ? 'Try adjusting your filters or search query'
                          : 'Create a work order to schedule maintenance tasks'}
                      </div>
                      {!hasActiveFilters && (
                        <button
                          className="btn btn-primary mt-4"
                          onClick={() => navigateTo('workorder-create')}
                        >
                          {renderIcon('M12 4v16m8-8H4')}
                          Create Work Order
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // IoT Device Detail Modal
  const IoTDeviceDetailModal = () => {
    if (!selectedIotDevice) return null

    const deviceTypeLabels: Record<string, string> = {
      OCCUPANCY_SENSOR: 'Occupancy Sensor',
      SOLAR_MONITOR: 'Solar Monitor',
      WASTE_LEVEL: 'Waste Level',
      AIR_QUALITY: 'Air Quality',
      TEMPERATURE: 'Temperature',
      HUMIDITY: 'Humidity',
      NOISE_LEVEL: 'Noise Level',
      LIGHT_SENSOR: 'Light Sensor',
      STRUCTURAL_MONITOR: 'Structural Monitor',
    }

    const getProductName = (assetId: string) => {
      const product = products.find(p => p.id === assetId)
      return product?.name || assetId
    }

    const getProduct = (assetId: string) => {
      return products.find(p => p.id === assetId) || null
    }

    const formatValue = (value: number, unit: string) => {
      if (unit === 'lux') return value.toLocaleString()
      if (unit === 'mm') return value.toFixed(2)
      if (unit === '°C') return value.toFixed(1)
      return value.toString()
    }

    return (
      <Modal
        isOpen={iotDeviceDetailModalOpen}
        onClose={() => {
          setIotDeviceDetailModalOpen(false)
          setSelectedIotDevice(null)
          setSensorReadings([])
        }}
        title="Device Details"
        maxWidth="3xl"
      >
        <div className="space-y-6">
          {/* Device Header */}
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{selectedIotDevice.id}</h3>
              <p className="text-gray-500">{deviceTypeLabels[selectedIotDevice.deviceType] || selectedIotDevice.deviceType}</p>
            </div>
            <IoTStatusBadge status={selectedIotDevice.status} />
          </div>

          {/* Device Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Manufacturer</div>
              <div className="font-medium text-gray-900">{selectedIotDevice.manufacturer || '-'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Model</div>
              <div className="font-medium text-gray-900">{selectedIotDevice.model || '-'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Firmware</div>
              <div className="font-medium text-gray-900">{selectedIotDevice.firmwareVersion || '-'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide">Signal</div>
              <div className="font-medium text-gray-900">{selectedIotDevice.signalStrength ? `${selectedIotDevice.signalStrength} dBm` : '-'}</div>
            </div>
          </div>

          {/* Battery & Asset */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedIotDevice.batteryLevel !== undefined && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-500">Battery Level</span>
                  <span className={`font-semibold ${selectedIotDevice.batteryLevel > 50 ? 'text-success-600' : selectedIotDevice.batteryLevel > 20 ? 'text-warning-600' : 'text-error-600'}`}>
                    {selectedIotDevice.batteryLevel}%
                  </span>
                </div>
                <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      selectedIotDevice.batteryLevel > 50 ? 'bg-success-500' :
                      selectedIotDevice.batteryLevel > 20 ? 'bg-warning-500' : 'bg-error-500'
                    }`}
                    style={{ width: `${selectedIotDevice.batteryLevel}%` }}
                  />
                </div>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Linked Asset</div>
              <button
                className="text-esf-navy hover:underline font-medium"
                onClick={() => {
                  const product = getProduct(selectedIotDevice.assetId)
                  if (product) {
                    setIotDeviceDetailModalOpen(false)
                    setSelectedIotDevice(null)
                    setSelectedProduct(product)
                    setProductDetailModalOpen(true)
                  }
                }}
              >
                {getProductName(selectedIotDevice.assetId)}
              </button>
              <div className="text-xs text-gray-400 mt-1">{selectedIotDevice.assetId}</div>
            </div>
          </div>

          {/* Sensor Readings */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900">Recent Sensor Readings</h4>
              <button
                className="text-sm text-esf-navy hover:underline"
                onClick={() => fetchSensorReadings(selectedIotDevice.id)}
              >
                Refresh
              </button>
            </div>
            {sensorReadingsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading readings...</div>
            ) : sensorReadings.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Timestamp</th>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Type</th>
                      <th className="text-right px-4 py-2 font-medium text-gray-600">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sensorReadings.slice(0, 20).map((reading, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-500">
                          {new Date(reading.timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-gray-700">
                          {deviceTypeLabels[reading.sensorType] || reading.sensorType}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {formatValue(reading.value, reading.unit)} <span className="text-gray-400">{reading.unit}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {sensorReadings.length > 20 && (
                  <div className="text-center py-2 text-sm text-gray-500 bg-gray-50">
                    Showing 20 of {sensorReadings.length} readings
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                No sensor readings available for this device.
              </div>
            )}
          </div>

          {/* Last Seen */}
          <div className="text-sm text-gray-500 border-t pt-4">
            Last seen: {selectedIotDevice.lastSeenAt ? new Date(selectedIotDevice.lastSeenAt).toLocaleString() : 'Never'}
          </div>
        </div>
      </Modal>
    )
  }

  // ESG Reports Page - DEPRECATED: Now using imported ESGPage component
  const _OldESGPage = () => {
    const [esgPeriod, setEsgPeriod] = useState<'MONTHLY' | 'QUARTERLY' | 'YEARLY'>('MONTHLY')

    // Filter and sort ESG metrics
    const filteredAndSortedMetrics = useMemo(() => {
      let filtered = esgMetrics.filter(m => {
        // Filter by source
        if (esgSourceFilter && m.source !== esgSourceFilter) return false

        // Filter by date range
        if (esgDateFrom) {
          const fromDate = new Date(esgDateFrom)
          const metricDate = new Date(m.periodStart)
          if (metricDate < fromDate) return false
        }
        if (esgDateTo) {
          const toDate = new Date(esgDateTo)
          const metricDate = new Date(m.periodStart)
          if (metricDate > toDate) return false
        }

        // Filter by period type
        if (m.period !== esgPeriod) return false

        return true
      })

      // Sort
      filtered.sort((a, b) => {
        let aVal: number, bVal: number
        switch (esgSortBy) {
          case 'carbonSaved':
            aVal = a.carbonSaved || 0
            bVal = b.carbonSaved || 0
            break
          case 'energyGenerated':
            aVal = a.energyGenerated || 0
            bVal = b.energyGenerated || 0
            break
          case 'wasteCollected':
            aVal = a.wasteCollected || 0
            bVal = b.wasteCollected || 0
            break
          case 'recyclingRate':
            aVal = a.recyclingRate || 0
            bVal = b.recyclingRate || 0
            break
          case 'periodStart':
          default:
            aVal = new Date(a.periodStart).getTime()
            bVal = new Date(b.periodStart).getTime()
        }
        return esgSortDir === 'asc' ? aVal - bVal : bVal - aVal
      })

      return filtered
    }, [esgMetrics, esgSourceFilter, esgDateFrom, esgDateTo, esgPeriod, esgSortBy, esgSortDir])

    // Calculate aggregate metrics from filtered data
    const totalCarbonSaved = filteredAndSortedMetrics.reduce((sum, m) => sum + (m.carbonSaved || 0), 0)
    const totalEnergyGenerated = filteredAndSortedMetrics.reduce((sum, m) => sum + (m.energyGenerated || 0), 0)
    const totalWasteCollected = filteredAndSortedMetrics.reduce((sum, m) => sum + (m.wasteCollected || 0), 0)
    const avgRecyclingRate = filteredAndSortedMetrics.length > 0
      ? filteredAndSortedMetrics.reduce((sum, m) => sum + (m.recyclingRate || 0), 0) / filteredAndSortedMetrics.length
      : 0

    const hasActiveFilters = esgSourceFilter || esgDateFrom || esgDateTo

    const clearFilters = () => {
      setEsgSourceFilter('')
      setEsgDateFrom('')
      setEsgDateTo('')
    }

    // Sort column click handler
    const handleSort = (column: typeof esgSortBy) => {
      if (esgSortBy === column) {
        setEsgSortDir(esgSortDir === 'asc' ? 'desc' : 'asc')
      } else {
        setEsgSortBy(column)
        setEsgSortDir('desc')
      }
    }

    // Sort indicator (as a function, not a component, to avoid re-render issues)
    const renderSortIndicator = (column: typeof esgSortBy) => {
      if (esgSortBy !== column) return <span className="text-gray-300 ml-1">↕</span>
      return <span className="text-primary-600 ml-1">{esgSortDir === 'asc' ? '↑' : '↓'}</span>
    }

    // CSV Export
    const exportToCSV = () => {
      const headers = ['Period', 'Period Start', 'Period End', 'Carbon Saved (kg)', 'Energy Generated (kWh)', 'Waste Collected (kg)', 'Recycling Rate (%)', 'Public Usage', 'Source']
      const rows = filteredAndSortedMetrics.map(m => [
        m.period,
        m.periodStart,
        m.periodEnd,
        m.carbonSaved || '',
        m.energyGenerated || '',
        m.wasteCollected || '',
        m.recyclingRate || '',
        m.publicUsageCount || '',
        m.source
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `esg-report-${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
    }

    // S3 Export
    const exportToS3 = async () => {
      setEsgExporting(true)
      try {
        const headers = ['Period', 'Period Start', 'Period End', 'Carbon Saved (kg)', 'Energy Generated (kWh)', 'Waste Collected (kg)', 'Recycling Rate (%)', 'Public Usage', 'Source']
        const rows = filteredAndSortedMetrics.map(m => [
          m.period,
          m.periodStart,
          m.periodEnd,
          m.carbonSaved || '',
          m.energyGenerated || '',
          m.wasteCollected || '',
          m.recyclingRate || '',
          m.publicUsageCount || '',
          m.source
        ])

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n')

        const response = await fetch(`${getApiBase()}/esg/export`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: csvContent,
            filename: `esg-report-${new Date().toISOString().split('T')[0]}.csv`
          })
        })

        if (!response.ok) throw new Error('Export failed')

        const result = await response.json()
        setAlertModal({
          isOpen: true,
          title: 'Export Successful',
          message: `Report saved successfully! Click "View Saved Reports" to access it.`,
          type: 'success'
        })
        // Refresh the saved reports list
        fetchSavedReports()
      } catch (err) {
        setAlertModal({
          isOpen: true,
          title: 'Export Failed',
          message: 'Failed to save report to online storage. Please try again.',
          type: 'error'
        })
      } finally {
        setEsgExporting(false)
      }
    }

    // Fetch saved reports from S3
    const fetchSavedReports = async () => {
      setSavedReportsLoading(true)
      try {
        const response = await fetch(`${getApiBase()}/esg/reports`)
        if (!response.ok) throw new Error('Failed to fetch')
        const data = await response.json()
        setSavedReports(data.reports || [])
      } catch (err) {
        console.error('Failed to fetch saved reports:', err)
        setSavedReports([])
      } finally {
        setSavedReportsLoading(false)
      }
    }

    // Download a saved report (proxied through API)
    const downloadReport = (key: string, filename: string) => {
      // Navigate directly to the download endpoint - it streams the file with Content-Disposition: attachment
      window.location.href = `${getApiBase()}/esg/reports/download?key=${encodeURIComponent(key)}`
    }

    // Format file size
    const formatFileSize = (bytes: number) => {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    // Load saved reports when modal opens
    const handleOpenSavedReports = () => {
      setShowSavedReports(true)
      fetchSavedReports()
    }

    return (
      <div className="container-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">ESG Reports</h1>
          <div className="flex gap-2">
            <button onClick={fetchEsgMetrics} className="btn btn-secondary">
              {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
              Refresh
            </button>
            <button onClick={exportToCSV} className="btn btn-secondary" disabled={filteredAndSortedMetrics.length === 0}>
              {renderIcon('M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
              Export CSV
            </button>
            <button
              onClick={exportToS3}
              className="btn btn-primary"
              disabled={filteredAndSortedMetrics.length === 0 || esgExporting}
            >
              {esgExporting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  {renderIcon('M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12')}
                  Save to Online Storage
                </>
              )}
            </button>
            <button
              onClick={handleOpenSavedReports}
              className="btn btn-outline"
            >
              {renderIcon('M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4')}
              View Saved Reports
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="card mb-6">
          <div className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                <select
                  className="filter-select"
                  value={esgPeriod}
                  onChange={e => setEsgPeriod(e.target.value as typeof esgPeriod)}
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="YEARLY">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  className="filter-select"
                  value={esgSourceFilter}
                  onChange={e => setEsgSourceFilter(e.target.value)}
                >
                  <option value="">All Sources</option>
                  <option value="AUTOMATED">Automated</option>
                  <option value="MANUAL">Manual</option>
                  <option value="IMPORTED">Imported</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  className="filter-select"
                  value={esgDateFrom}
                  onChange={e => setEsgDateFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  className="filter-select"
                  value={esgDateTo}
                  onChange={e => setEsgDateTo(e.target.value)}
                />
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="btn btn-outline text-sm"
                >
                  Clear Filters
                </button>
              )}
            </div>
            {hasActiveFilters && (
              <div className="mt-3 text-sm text-gray-500">
                Showing {filteredAndSortedMetrics.length} of {esgMetrics.filter(m => m.period === esgPeriod).length} {esgPeriod.toLowerCase()} records
              </div>
            )}
          </div>
        </div>

        {/* ESG KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="kpi">
            <div className="kpi-icon bg-success-100 text-success-600">
              {renderIcon('M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z')}
            </div>
            <div className="kpi-title">Carbon Saved</div>
            <div className="kpi-value">{(totalCarbonSaved / 1000).toFixed(1)}t</div>
            <div className="kpi-change positive">CO2e avoided</div>
          </div>
          <div className="kpi">
            <div className="kpi-icon bg-warning-100 text-warning-600">
              {renderIcon('M13 10V3L4 14h7v7l9-11h-7z')}
            </div>
            <div className="kpi-title">Energy Generated</div>
            <div className="kpi-value">{totalEnergyGenerated.toLocaleString()}</div>
            <div className="kpi-change positive">kWh solar</div>
          </div>
          <div className="kpi">
            <div className="kpi-icon bg-info-100 text-info-600">
              {renderIcon('M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16')}
            </div>
            <div className="kpi-title">Waste Collected</div>
            <div className="kpi-value">{(totalWasteCollected / 1000).toFixed(1)}t</div>
            <div className="kpi-change positive">Total waste</div>
          </div>
          <div className="kpi">
            <div className="kpi-icon bg-purple-100 text-purple-600">
              {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
            </div>
            <div className="kpi-title">Recycling Rate</div>
            <div className="kpi-value">{avgRecyclingRate.toFixed(0)}%</div>
            <div className="kpi-change positive">Average</div>
          </div>
        </div>

        {/* ESG Metrics Table */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="card-title">ESG Metrics History</h2>
            <span className="text-sm text-gray-500">
              Click column headers to sort
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('periodStart')}
                  >
                    Period {renderSortIndicator('periodStart')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('carbonSaved')}
                  >
                    Carbon Saved {renderSortIndicator('carbonSaved')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('energyGenerated')}
                  >
                    Energy Generated {renderSortIndicator('energyGenerated')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('wasteCollected')}
                  >
                    Waste Collected {renderSortIndicator('wasteCollected')}
                  </th>
                  <th
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('recyclingRate')}
                  >
                    Recycling Rate {renderSortIndicator('recyclingRate')}
                  </th>
                  <th>Public Usage</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedMetrics.map(metric => (
                  <tr key={metric.id}>
                    <td className="font-medium">
                      {new Date(metric.periodStart).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </td>
                    <td>{metric.carbonSaved ? `${metric.carbonSaved.toLocaleString()} kg` : '-'}</td>
                    <td>{metric.energyGenerated ? `${metric.energyGenerated.toLocaleString()} kWh` : '-'}</td>
                    <td>{metric.wasteCollected ? `${metric.wasteCollected.toLocaleString()} kg` : '-'}</td>
                    <td>{metric.recyclingRate ? `${metric.recyclingRate}%` : '-'}</td>
                    <td>{metric.publicUsageCount?.toLocaleString() || '-'}</td>
                    <td>
                      <span className={`badge ${
                        metric.source === 'AUTOMATED' ? 'bg-success-100 text-success-700' :
                        metric.source === 'MANUAL' ? 'bg-info-100 text-info-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {metric.source}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredAndSortedMetrics.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty-state">
                        <div className="empty-state-icon bg-success-100 text-success-600">
                          {renderIcon('M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z')}
                        </div>
                        <div className="empty-state-title">No ESG Data</div>
                        <div className="empty-state-text">
                          {hasActiveFilters
                            ? 'No records match your filter criteria. Try adjusting the filters.'
                            : 'ESG metrics will be collected automatically from your IoT devices and asset data.'}
                        </div>
                        {hasActiveFilters && (
                          <button onClick={clearFilters} className="btn btn-secondary mt-4">
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Saved Reports Modal */}
        <Modal
          isOpen={showSavedReports}
          onClose={() => setShowSavedReports(false)}
          title="Saved Reports"
          size="lg"
        >
          <div className="space-y-4">
            {savedReportsLoading ? (
              <div className="flex items-center justify-center py-8">
                <span className="animate-spin inline-block w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full mr-3" />
                Loading saved reports...
              </div>
            ) : savedReports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="mb-2">
                  {renderIcon('M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4')}
                </div>
                <p>No saved reports yet.</p>
                <p className="text-sm mt-1">Use "Save to Online Storage" to save your first report.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {savedReports.map((report, index) => (
                  <div key={report.key || index} className="py-3 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {report.filename || report.key?.split('/').pop()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(report.lastModified).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {report.size && ` • ${formatFileSize(report.size)}`}
                      </p>
                    </div>
                    <button
                      onClick={() => downloadReport(report.key, report.filename || 'report.csv')}
                      className="btn btn-sm btn-secondary ml-4"
                    >
                      {renderIcon('M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z')}
                      Download
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-4 border-t">
              <button
                onClick={() => setShowSavedReports(false)}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      </div>
    )
  }

  // Users Page - DEPRECATED: Now using imported UsersPage component
  const _OldUsersPage = () => {
    const roleColors: Record<string, string> = {
      OrgAdmin: 'bg-purple-100 text-purple-700',
      AssetManager: 'bg-blue-100 text-blue-700',
      FieldTech: 'bg-green-100 text-green-700',
      Analyst: 'bg-yellow-100 text-yellow-700',
      PublicViewer: 'bg-gray-100 text-gray-700',
    }

    return (
      <div className="container-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <button
            className="btn btn-primary"
            onClick={() => {
              setSelectedUser(null)
              setUserModalOpen(true)
            }}
          >
            {renderIcon('M12 4v16m8-8H4')}
            Add User
          </button>
        </div>

        {/* Users Table */}
        <div className="table-card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.email}>
                  <td className="font-medium">{u.name}</td>
                  <td className="text-sm text-gray-600">{u.email}</td>
                  <td>
                    <span className={`badge ${roleColors[u.role] || 'bg-gray-100 text-gray-700'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="text-sm text-gray-500">{u.phoneNumber || '-'}</td>
                  <td>
                    <span className={`badge ${u.enabled ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
                      {u.enabled ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="text-sm text-gray-500">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : 'Never'}
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setSelectedUser(u)
                          setUserModalOpen(true)
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className={`btn btn-sm ${u.enabled ? 'btn-secondary' : 'bg-success-50 text-success-600 hover:bg-success-100'}`}
                        onClick={() => handleToggleUserEnabled(u)}
                        disabled={isSubmitting}
                      >
                        {u.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        className="btn btn-sm bg-error-50 text-error-600 hover:bg-error-100"
                        onClick={() => {
                          setUserToDelete(u)
                          setDeleteUserModalOpen(true)
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-state-icon bg-purple-100 text-purple-600">
                        {renderIcon('M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z')}
                      </div>
                      <div className="empty-state-title">No Users</div>
                      <div className="empty-state-text">
                        Add team members to manage your organisation's assets.
                      </div>
                      <button
                        className="btn btn-primary mt-4"
                        onClick={() => {
                          setSelectedUser(null)
                          setUserModalOpen(true)
                        }}
                      >
                        {renderIcon('M12 4v16m8-8H4')}
                        Add User
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // DPP Templates Page - DEPRECATED: Now using imported DppTemplatesPage component
  const _OldDppTemplatesPage = () => {
    const [mpCategoryFilter, setMpCategoryFilter] = useState('')
    const [mpSearchQuery, setMpSearchQuery] = useState('')
    const [mpActiveFilter, setMpActiveFilter] = useState('')

    const filteredMasterProducts = useMemo(() => {
      let filtered = masterProducts
      if (mpSearchQuery) {
        const q = mpSearchQuery.toLowerCase()
        filtered = filtered.filter(mp =>
          mp.name?.toLowerCase().includes(q) ||
          mp.model?.toLowerCase().includes(q) ||
          mp.sku?.toLowerCase().includes(q)
        )
      }
      if (mpCategoryFilter) {
        filtered = filtered.filter(mp => mp.category === mpCategoryFilter)
      }
      if (mpActiveFilter !== '') {
        filtered = filtered.filter(mp => mp.isActive === (mpActiveFilter === 'true'))
      }
      return filtered
    }, [masterProducts, mpSearchQuery, mpCategoryFilter, mpActiveFilter])

    return (
      <div className="container-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">DPP Templates</h1>
            <p className="text-sm text-gray-500 mt-1">Digital Product Passport templates for creating new assets with pre-populated data</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => navigateTo('dpp-template-create')}
          >
            {renderIcon('M12 4v16m8-8H4')}
            Create Template
          </button>
        </div>

        {/* Filters */}
        <div className="filter-bar">
          <input
            type="text"
            placeholder="Search templates..."
            className="filter-input flex-1"
            value={mpSearchQuery}
            onChange={e => setMpSearchQuery(e.target.value)}
          />
          <select
            className="filter-select"
            value={mpCategoryFilter}
            onChange={e => setMpCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {ProductCategories.map(c => <option key={c} value={c}>{ProductCategoryLabels[c]}</option>)}
          </select>
          <select
            className="filter-select"
            value={mpActiveFilter}
            onChange={e => setMpActiveFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button onClick={fetchMasterProducts} className="btn btn-secondary">
            {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
          </button>
        </div>

        {/* DPP Templates Table */}
        <div className="table-card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Model</th>
                <th>SKU</th>
                <th>Manufacturer</th>
                <th>Status</th>
                <th>Version</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredMasterProducts.map(mp => (
                <tr key={mp.id}>
                  <td className="font-medium">{mp.name}</td>
                  <td><CategoryBadge category={mp.category} /></td>
                  <td className="text-sm text-gray-600">{mp.model || '-'}</td>
                  <td className="font-mono text-sm text-gray-500">{mp.sku || '-'}</td>
                  <td className="text-sm text-gray-600">{mp.manufacturer || '-'}</td>
                  <td>
                    <span className={`badge ${mp.isActive ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-500'}`}>
                      {mp.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-sm text-gray-500">v{mp.version}</td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setSelectedMasterProduct(mp)
                          setMasterProductDetailModalOpen(true)
                        }}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => navigateTo('dpp-template-edit', mp.id)}
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredMasterProducts.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        {renderIcon('M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z')}
                      </div>
                      <div className="empty-state-title">No DPP templates found</div>
                      <div className="empty-state-text">
                        {mpSearchQuery || mpCategoryFilter || mpActiveFilter
                          ? 'Try adjusting your filters'
                          : 'Create a DPP template to streamline asset creation'}
                      </div>
                      {!mpSearchQuery && !mpCategoryFilter && !mpActiveFilter && (
                        <button
                          className="btn btn-primary mt-4"
                          onClick={() => navigateTo('dpp-template-create')}
                        >
                          {renderIcon('M12 4v16m8-8H4')}
                          Create Template
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Settings Page - DEPRECATED: Now using imported SettingsPage component
  const _OldSettingsPage = () => {
    const [orgName, setOrgName] = useState('Environmental Street Furniture')
    const [orgEmail, setOrgEmail] = useState('info@worldofesf.com')
    const [sessionTimeout, setSessionTimeout] = useState(30)
    const [isSaving, setIsSaving] = useState(false)

    const handleSaveSettings = async () => {
      setIsSaving(true)
      // Simulate save
      await new Promise(resolve => setTimeout(resolve, 1000))
      setIsSaving(false)
      showAlert('Success', 'Settings saved successfully', 'success')
    }

    return (
      <div className="container-page">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Organisation Settings */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Organisation</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Name</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={e => setOrgName(e.target.value)}
                  className="filter-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={orgEmail}
                  onChange={e => setOrgEmail(e.target.value)}
                  className="filter-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Logo</label>
                <div className="flex items-center gap-4">
                  <img src="/logo.svg" alt="Logo" className="h-12 w-auto bg-gray-100 rounded p-2" />
                  <button className="btn btn-sm btn-outline">Change Logo</button>
                </div>
              </div>
            </div>
          </div>

          {/* Security Settings */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Security</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Session Timeout (minutes)</label>
                <input
                  type="number"
                  value={sessionTimeout}
                  onChange={e => setSessionTimeout(parseInt(e.target.value) || 30)}
                  min={5}
                  max={120}
                  className="filter-input w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Users will be logged out after this period of inactivity</p>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">Two-Factor Authentication</div>
                  <div className="text-sm text-gray-500">Require 2FA for all users</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-esf-navy/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-esf-navy"></div>
                </label>
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Notifications</h2>
            </div>
            <div className="card-body space-y-3">
              {[
                { label: 'Asset Alerts', desc: 'Notifications about asset status changes' },
                { label: 'Work Order Updates', desc: 'Updates on work order assignments and completions' },
                { label: 'IoT Alerts', desc: 'Sensor warnings and device status changes' },
                { label: 'ESG Reports', desc: 'Monthly sustainability report notifications' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{item.label}</div>
                    <div className="text-sm text-gray-500">{item.desc}</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-esf-navy/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-esf-navy"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Integration Settings */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Integrations</h2>
            </div>
            <div className="card-body space-y-3">
              {[
                { label: 'AWS IoT Core', status: 'Connected', icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z' },
                { label: 'LocalStack (Dev)', status: 'Active', icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01' },
                { label: 'Email Service', status: 'Configured', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                      {renderIcon(item.icon)}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{item.label}</div>
                      <div className="text-sm text-success-600">{item.status}</div>
                    </div>
                  </div>
                  <button className="btn btn-sm btn-outline">Configure</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={isSaving}
            className="btn btn-primary"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : 'Save Settings'}
          </button>
        </div>
      </div>
    )
  }

  // Render current page
  const renderPage = () => {
    switch (currentPage) {
      case 'admin-dashboard': return (
          <AdminDashboardPage
            stats={adminDashboardStats}
            organisations={organisations}
            selectedOrgId={selectedAdminOrgId}
            onSelectOrg={(orgId) => {
              setSelectedAdminOrgId(orgId)
              setUrlOrg(orgId)
              fetchAdminDashboard(orgId)
            }}
            onRefresh={() => fetchAdminDashboard(selectedAdminOrgId)}
            onNavigateToOrg={(orgId) => {
              setSelectedAdminOrgId(orgId)
              navigateTo('organisations')
            }}
            isLoading={adminDashboardLoading}
          />
        )
      case 'organisations': return (
          <OrganisationsPage
            organisations={organisations}
            orgStats={orgStats}
            onCreateOrg={() => {
              setSelectedOrg(null)
              setOrgModalOpen(true)
            }}
            onEditOrg={(org) => {
              setSelectedOrg(org)
              setOrgModalOpen(true)
            }}
            onToggleOrgEnabled={handleToggleOrgEnabled}
            onViewOrgDetails={(org) => {
              setViewingOrg(org)
              setOrgDetailModalOpen(true)
            }}
            isLoading={organisationsLoading}
          />
        )
      case 'dashboard': return (
          <DashboardPage
            products={allProducts}
            analytics={analytics}
            fetchProducts={fetchAllProducts}
            fetchAnalytics={fetchAnalytics}
            navigateTo={navigateTo}
            navigateWithFilters={navigateWithFilters}
            setSelectedProduct={setSelectedProduct}
            setProductDetailModalOpen={setProductDetailModalOpen}
          />
        )
      case 'assets': return (
          <ProductsPage
            products={products}
            loading={loading}
            error={error}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            showDeleted={showDeletedProducts}
            setShowDeleted={setShowDeletedProducts}
            navigateTo={navigateTo}
            fetchProducts={fetchProducts}
            setSelectedProduct={setSelectedProduct}
            setProductDetailModalOpen={setProductDetailModalOpen}
            onRestoreProduct={handleRestoreProduct}
          />
        )
      case 'dpp-templates': return (
          <DppTemplatesPage
            masterProducts={masterProducts}
            navigateTo={navigateTo}
            setSelectedMasterProduct={setSelectedMasterProduct}
            setMasterProductDetailModalOpen={setMasterProductDetailModalOpen}
            fetchMasterProducts={fetchMasterProducts}
            showDeleted={showDeletedMasterProducts}
            setShowDeleted={setShowDeletedMasterProducts}
            onRestoreMasterProduct={handleRestoreMasterProduct}
          />
        )
      case 'dpp-template-create':
        return (
          <DppTemplateFormPage
            onComplete={() => {
              fetchMasterProducts()
              navigateTo('dpp-templates')
              showAlert('Success', 'DPP template created successfully', 'success')
            }}
            onCancel={() => navigateTo('dpp-templates')}
          />
        )
      case 'dpp-template-edit':
        return (
          <DppTemplateFormPage
            templateId={editDppTemplateId}
            onComplete={() => {
              fetchMasterProducts()
              navigateTo('dpp-templates')
              showAlert('Success', 'DPP template updated successfully', 'success')
            }}
            onCancel={() => navigateTo('dpp-templates')}
          />
        )
      case 'product-create':
        return (
          <ProductFormPage
            masterProducts={masterProducts}
            onComplete={() => {
              fetchProducts()
              fetchAllProducts()
              fetchAnalytics()
              navigateTo('assets')
              showAlert('Success', 'Product created successfully', 'success')
            }}
            onCancel={() => navigateTo('assets')}
          />
        )
      case 'product-edit':
        return (
          <ProductFormPage
            productId={editProductId}
            masterProducts={masterProducts}
            onComplete={() => {
              fetchProducts()
              fetchAllProducts()
              fetchAnalytics()
              navigateTo('assets')
              showAlert('Success', 'Product updated successfully', 'success')
            }}
            onCancel={() => navigateTo('assets')}
          />
        )
      case 'iot': return (
          <IoTDashboardPage
            iotDevices={iotDevices}
            products={products}
            iotStatusFilter={iotStatusFilter}
            setIotStatusFilter={setIotStatusFilter}
            iotTypeFilter={iotTypeFilter}
            setIotTypeFilter={setIotTypeFilter}
            iotSearchQuery={iotSearchQuery}
            setIotSearchQuery={setIotSearchQuery}
            iotSortBy={iotSortBy}
            setIotSortBy={setIotSortBy}
            iotSortDir={iotSortDir}
            setIotSortDir={setIotSortDir}
            iotExporting={iotExporting}
            setIotExporting={setIotExporting}
            showIotSavedReports={showIotSavedReports}
            setShowIotSavedReports={setShowIotSavedReports}
            iotSavedReports={iotSavedReports}
            setIotSavedReports={setIotSavedReports}
            iotSavedReportsLoading={iotSavedReportsLoading}
            setIotSavedReportsLoading={setIotSavedReportsLoading}
            setSelectedIotDevice={setSelectedIotDevice}
            setIotDeviceDetailModalOpen={setIotDeviceDetailModalOpen}
            setSelectedProduct={setSelectedProduct}
            setProductDetailModalOpen={setProductDetailModalOpen}
            fetchSensorReadings={fetchSensorReadings}
            fetchIotDevices={fetchIotDevices}
            setAlertModal={setAlertModal}
          />
        )
      case 'esg': return (
          <ESGPage
            esgMetrics={esgMetrics}
            fetchEsgMetrics={fetchEsgMetrics}
            esgSourceFilter={esgSourceFilter}
            setEsgSourceFilter={setEsgSourceFilter}
            esgDateFrom={esgDateFrom}
            setEsgDateFrom={setEsgDateFrom}
            esgDateTo={esgDateTo}
            setEsgDateTo={setEsgDateTo}
            esgSortBy={esgSortBy}
            setEsgSortBy={setEsgSortBy}
            esgSortDir={esgSortDir}
            setEsgSortDir={setEsgSortDir}
            esgExporting={esgExporting}
            setEsgExporting={setEsgExporting}
            showSavedReports={showSavedReports}
            setShowSavedReports={setShowSavedReports}
            savedReports={savedReports}
            setSavedReports={setSavedReports}
            savedReportsLoading={savedReportsLoading}
            setSavedReportsLoading={setSavedReportsLoading}
            setAlertModal={setAlertModal}
          />
        )
      case 'workorders': return (
          <WorkOrdersPage
            products={products}
            workOrders={workOrders}
            workOrderStatusFilter={workOrderStatusFilter}
            setWorkOrderStatusFilter={setWorkOrderStatusFilter}
            workOrderPriorityFilter={workOrderPriorityFilter}
            setWorkOrderPriorityFilter={setWorkOrderPriorityFilter}
            workOrderTypeFilter={workOrderTypeFilter}
            setWorkOrderTypeFilter={setWorkOrderTypeFilter}
            workOrderAssetFilter={workOrderAssetFilter}
            setWorkOrderAssetFilter={setWorkOrderAssetFilter}
            workOrderAssigneeFilter={workOrderAssigneeFilter}
            setWorkOrderAssigneeFilter={setWorkOrderAssigneeFilter}
            workOrderSearchQuery={workOrderSearchQuery}
            setWorkOrderSearchQuery={setWorkOrderSearchQuery}
            showDeleted={showDeletedWorkOrders}
            setShowDeleted={setShowDeletedWorkOrders}
            navigateTo={navigateTo}
            fetchWorkOrders={fetchWorkOrders}
            setSelectedWorkOrder={setSelectedWorkOrder}
            setWorkOrderDetailModalOpen={setWorkOrderDetailModalOpen}
            onRestoreWorkOrder={handleRestoreWorkOrder}
          />
        )
      case 'workorder-create':
        return (
          <WorkOrderFormPage
            preselectedAssetId={workOrderCreateAssetId}
            onComplete={() => {
              fetchWorkOrders()
              navigateTo('workorders')
              showAlert('Success', 'Work order created successfully', 'success')
            }}
            onCancel={() => navigateTo('workorders')}
          />
        )
      case 'workorder-edit':
        return (
          <WorkOrderFormPage
            workOrderId={editWorkOrderId}
            onComplete={() => {
              fetchWorkOrders()
              navigateTo('workorders')
              showAlert('Success', 'Work order updated successfully', 'success')
            }}
            onCancel={() => navigateTo('workorders')}
          />
        )
      case 'users': return (
          <UsersPage
            users={users}
            setSelectedUser={setSelectedUser}
            setUserModalOpen={setUserModalOpen}
            handleToggleUserEnabled={handleToggleUserEnabled}
            handlePasswordReset={handlePasswordReset}
            isSubmitting={isSubmitting}
            setUserToDelete={setUserToDelete}
            setDeleteUserModalOpen={setDeleteUserModalOpen}
            isSuperAdmin={isSuperAdmin}
            organisations={organisations}
            selectedOrgFilter={selectedUserOrgFilter}
            onOrgFilterChange={setSelectedUserOrgFilter}
            showDeleted={showDeletedUsers}
            setShowDeleted={setShowDeletedUsers}
            onRestoreUser={handleRestoreUser}
          />
        )
      case 'audit-logs': return (
          <AuditLogsPage
            auditLogs={auditLogs}
            loading={auditLogsLoading}
            nextKey={auditNextKey}
            actionFilter={auditActionFilter}
            setActionFilter={setAuditActionFilter}
            actorFilter={auditActorFilter}
            setActorFilter={setAuditActorFilter}
            fromDate={auditFromDate}
            setFromDate={setAuditFromDate}
            toDate={auditToDate}
            setToDate={setAuditToDate}
            searchQuery={auditSearchQuery}
            setSearchQuery={setAuditSearchQuery}
            orgFilter={auditOrgFilter}
            setOrgFilter={setAuditOrgFilter}
            isSuperAdmin={isSuperAdmin}
            organisations={organisations}
            fetchAuditLogs={fetchAuditLogs}
          />
        )
      case 'settings': return (
          <SettingsPage
            showAlert={showAlert}
          />
        )
      case 'tutorial': return (
          <TutorialPage
            navigateTo={navigateTo}
            userRole={(user?.role || debugRole) as string}
          />
        )
      default: return (
          <DashboardPage
            products={allProducts}
            analytics={analytics}
            fetchProducts={fetchAllProducts}
            fetchAnalytics={fetchAnalytics}
            navigateTo={navigateTo}
            navigateWithFilters={navigateWithFilters}
            setSelectedProduct={setSelectedProduct}
            setProductDetailModalOpen={setProductDetailModalOpen}
          />
        )
    }
  }

  let lastSection = ''

  // Show loading state while checking auth
  if (authChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-esf-navy mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Show login page if not authenticated
  if (!isAuthenticated && !debugMode) {
    return <LoginPage base={base} onLoginSuccess={handleLoginSuccess} />
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="logo bg-white">
          <img src="/logo.svg" alt="ESF" className="h-8 w-auto" />
        </div>
        <nav>
          {navItems.map(item => {
            const showSection = item.section && item.section !== lastSection
            if (item.section) lastSection = item.section
            return (
              <div key={item.id}>
                {showSection && <div className="nav-section">{item.section}</div>}
                <a
                  href={`#/${item.id}`}
                  className={currentPage === item.id ? 'active' : ''}
                  onClick={e => { e.preventDefault(); navigateTo(item.id as Page) }}
                >
                  {renderIcon(item.icon)}
                  {item.label}
                </a>
              </div>
            )
          })}
        </nav>
      </aside>

      {/* Header */}
      <header className="header-bar">
        <div className="header-inner">

          {/* Global Smart Search */}
          <div className="flex-1 max-w-xl mx-4 relative" ref={globalSearchRef}>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-esf-navy/20 focus:border-esf-navy transition-colors"
                placeholder="Search assets, work orders, users, devices..."
                value={globalSearch}
                onChange={(e) => handleGlobalSearch(e.target.value)}
                onFocus={() => globalSearch && setShowGlobalSearchResults(true)}
              />
            </div>

            {/* Search Results Dropdown */}
            {showGlobalSearchResults && globalSearchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-96 overflow-y-auto z-50">
                {globalSearchResults.map((result, idx) => (
                  <button
                    key={`${result.type}-${result.id}-${idx}`}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${
                        result.type === 'asset' ? 'bg-esf-navy/10 text-esf-navy' :
                        result.type === 'workorder' ? 'bg-amber-100 text-amber-700' :
                        result.type === 'user' ? 'bg-blue-100 text-blue-700' :
                        result.type === 'template' ? 'bg-purple-100 text-purple-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {result.type === 'asset' && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        )}
                        {result.type === 'workorder' && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                        )}
                        {result.type === 'user' && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        )}
                        {result.type === 'iot' && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        )}
                        {result.type === 'template' && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{result.title}</div>
                        <div className="text-sm text-gray-500 truncate">{result.subtitle}</div>
                        {result.location && (
                          <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">{result.location}</span>
                          </div>
                        )}
                      </div>
                      <div className={`text-xs uppercase font-medium px-2 py-0.5 rounded ${
                        result.type === 'asset' ? 'bg-esf-navy/10 text-esf-navy' :
                        result.type === 'workorder' ? 'bg-amber-100 text-amber-700' :
                        result.type === 'user' ? 'bg-blue-100 text-blue-700' :
                        result.type === 'template' ? 'bg-purple-100 text-purple-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {result.type === 'workorder' ? 'work order' : result.type === 'template' ? 'DPP template' : result.type}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No Results */}
            {showGlobalSearchResults && globalSearchResults.length === 0 && globalSearch && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl p-6 z-50">
                <div className="text-center text-gray-500">
                  <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-sm">No results found for "<span className="font-medium">{globalSearch}</span>"</p>
                  <p className="text-xs text-gray-400 mt-1">Try searching for assets, work orders, users, devices, or DPP templates</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Debug Role Selector */}
            {debugMode && (
              <select
                className="text-xs border border-gray-200 rounded px-2 py-1"
                value={debugRole}
                onChange={e => setDebugRole(e.target.value)}
              >
                {UserRoles.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            )}

            {/* Organisation Context - SuperAdmin badge or org name */}
            {((user?.role || debugRole) === 'SuperAdmin') ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-esf-coral/10 text-esf-coral border border-esf-coral/20">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  SuperAdmin
                </span>
              </div>
            ) : (user?.organisationName || user?.organisationId) && (
              <div className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                <span className="font-medium">{user?.organisationName || user?.organisationId}</span>
              </div>
            )}

            {/* Notifications */}
            <NotificationTray
              base={base}
              defaultHeaders={defaultHeaders}
              onNavigate={(path) => {
                // Handle notification links - use router for hash paths
                const cleanPath = path.startsWith('#/') ? path.substring(2) : path.startsWith('/') ? path.substring(1) : path
                const [pagePart, ...rest] = cleanPath.split('/')
                if (validPages.includes(pagePart as Page)) {
                  routerNavigate({ page: pagePart, id: rest.join('/') || undefined })
                }
              }}
            />
            {/* User avatar with dropdown */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="w-8 h-8 rounded-full bg-esf-navy text-white flex items-center justify-center text-sm font-medium hover:bg-esf-navy/90 transition-colors focus:outline-none focus:ring-2 focus:ring-esf-navy focus:ring-offset-2"
              >
                {user?.email?.[0]?.toUpperCase() || '?'}
              </button>
              {profileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-100">
                    <div className="text-sm font-medium text-gray-900">
                      {user?.email || (debugMode ? (debugEmailMap[debugRole] || `${debugRole.toLowerCase()}@esf.local`) : 'Not logged in')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {user?.role || (debugMode ? debugRole : '-')}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setProfileDropdownOpen(false)
                      handleLogout()
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="content">
        {renderPage()}
      </main>

      {/* Product Detail Modal */}
      <ProductDetailModal
        isOpen={productDetailModalOpen}
        onClose={() => {
          setProductDetailModalOpen(false)
          setSelectedProduct(null)
        }}
        product={selectedProduct}
        onEdit={(p) => {
          setProductDetailModalOpen(false)
          navigateTo('product-edit', p.id)
        }}
        onDelete={(p) => {
          setProductToDelete(p)
          setDeleteProductModalOpen(true)
        }}
        onCreateWorkOrder={(p) => {
          setProductDetailModalOpen(false)
          navigateToWorkOrderCreate(p.id)
        }}
        onRestore={(p) => {
          handleRestoreProduct(p)
        }}
      />

      {/* Delete Product Confirmation */}
      <ConfirmModal
        isOpen={deleteProductModalOpen}
        onClose={() => {
          setDeleteProductModalOpen(false)
          setProductToDelete(null)
        }}
        onConfirm={handleDeleteProduct}
        title="Delete Product"
        message={`Are you sure you want to delete "${productToDelete?.name}"?\n\nThe product will be moved to deleted items and can be restored later. Associated work orders and IoT device assignments may be affected.`}
        confirmText="Delete Product"
        type="danger"
      />

      {/* Work Order Detail Modal */}
      <Modal
        isOpen={workOrderDetailModalOpen}
        onClose={() => {
          setWorkOrderDetailModalOpen(false)
          setSelectedWorkOrder(null)
        }}
        title="Work Order Details"
        maxWidth="2xl"
      >
        {selectedWorkOrder && (
          <div className="space-y-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedWorkOrder.title}</h3>
                <p className="text-sm text-gray-500 font-mono mt-1">{selectedWorkOrder.id}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <WorkOrderStatusBadge status={selectedWorkOrder.status} />
                <PriorityBadge priority={selectedWorkOrder.priority} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Type</label>
                <p className="mt-1">{selectedWorkOrder.type.replace(/_/g, ' ')}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Asset</label>
                <p className="mt-1">{products.find(p => p.id === selectedWorkOrder.assetId)?.name || selectedWorkOrder.assetId}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Assigned To</label>
                <p className="mt-1">{selectedWorkOrder.assignedTo || '-'}</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Scheduled Date</label>
                <p className="mt-1">{selectedWorkOrder.scheduledDate ? new Date(selectedWorkOrder.scheduledDate).toLocaleDateString() : '-'}</p>
              </div>
            </div>

            {selectedWorkOrder.description && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Description</label>
                <p className="mt-1 whitespace-pre-wrap">{selectedWorkOrder.description}</p>
              </div>
            )}

            {selectedWorkOrder.notes && (
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase">Notes</label>
                <p className="mt-1 whitespace-pre-wrap bg-yellow-50 p-3 rounded-lg border border-yellow-100">{selectedWorkOrder.notes}</p>
              </div>
            )}

            {/* Status Actions */}
            <div className="border-t pt-4">
              <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">Quick Actions</label>
              <div className="flex flex-wrap gap-2">
                {selectedWorkOrder.status === 'PENDING' && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleUpdateWorkOrderStatus(selectedWorkOrder, 'SCHEDULED')}
                    disabled={isSubmitting}
                  >
                    Schedule
                  </button>
                )}
                {selectedWorkOrder.status === 'SCHEDULED' && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => handleUpdateWorkOrderStatus(selectedWorkOrder, 'IN_PROGRESS')}
                    disabled={isSubmitting}
                  >
                    Start Work
                  </button>
                )}
                {selectedWorkOrder.status === 'IN_PROGRESS' && (
                  <button
                    className="btn btn-sm bg-success-600 text-white hover:bg-success-700"
                    onClick={() => handleUpdateWorkOrderStatus(selectedWorkOrder, 'COMPLETED')}
                    disabled={isSubmitting}
                  >
                    Mark Complete
                  </button>
                )}
                {!['COMPLETED', 'CANCELLED'].includes(selectedWorkOrder.status) && (
                  <>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleUpdateWorkOrderStatus(selectedWorkOrder, 'ON_HOLD')}
                      disabled={isSubmitting}
                    >
                      Put On Hold
                    </button>
                    <button
                      className="btn btn-sm bg-error-50 text-error-600 hover:bg-error-100"
                      onClick={() => handleUpdateWorkOrderStatus(selectedWorkOrder, 'CANCELLED')}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <button
                className="btn btn-outline bg-error-50 text-error-600 hover:bg-error-100 border-error-200"
                onClick={() => {
                  setWorkOrderDetailModalOpen(false)
                  setWorkOrderToDelete(selectedWorkOrder)
                  setDeleteWorkOrderModalOpen(true)
                }}
              >
                Delete Work Order
              </button>
              <div className="flex gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => setWorkOrderDetailModalOpen(false)}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setWorkOrderDetailModalOpen(false)
                    if (selectedWorkOrder) {
                      navigateTo('workorder-edit', selectedWorkOrder.id)
                    }
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Work Order Confirmation */}
      <ConfirmModal
        isOpen={deleteWorkOrderModalOpen}
        onClose={() => {
          setDeleteWorkOrderModalOpen(false)
          setWorkOrderToDelete(null)
        }}
        onConfirm={handleDeleteWorkOrder}
        title="Delete Work Order"
        message={`Are you sure you want to delete work order "${workOrderToDelete?.title}"?\n\nThe work order will be moved to deleted items and can be restored later.`}
        confirmText="Delete"
        type="danger"
      />

      {/* User Create/Edit Modal */}
      <Modal
        isOpen={userModalOpen}
        onClose={() => {
          setUserModalOpen(false)
          setSelectedUser(null)
        }}
        title={selectedUser ? 'Edit User' : 'Add User'}
        maxWidth="xl"
      >
        <UserForm
          user={selectedUser}
          onSubmit={selectedUser ? handleUpdateUser : handleCreateUser}
          onCancel={() => {
            setUserModalOpen(false)
            setSelectedUser(null)
          }}
          isLoading={isSubmitting}
          currentUserRole={(user?.role || debugRole) as UserRole}
        />
      </Modal>

      {/* Delete User Confirmation */}
      <ConfirmModal
        isOpen={deleteUserModalOpen}
        onClose={() => {
          setDeleteUserModalOpen(false)
          setUserToDelete(null)
        }}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message={`Are you sure you want to delete user "${userToDelete?.name}" (${userToDelete?.email})?\n\nThe user will be moved to deleted items and lose access immediately. They can be restored later.`}
        confirmText="Delete User"
        type="danger"
      />

      {/* Reset Password Confirmation */}
      <ConfirmModal
        isOpen={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false)
          setUserToResetPassword(null)
        }}
        onConfirm={confirmPasswordReset}
        title="Reset Password"
        message={`Reset password for ${userToResetPassword?.email}?\n\nA temporary password will be generated and sent to the user's email.`}
        confirmText="Reset Password"
        type="warning"
      />

      {/* Organisation Create/Edit Modal */}
      <Modal
        isOpen={orgModalOpen}
        onClose={() => {
          setOrgModalOpen(false)
          setSelectedOrg(null)
        }}
        title={selectedOrg ? 'Edit Organisation' : 'Add Organisation'}
        maxWidth="xl"
      >
        <OrganisationForm
          organisation={selectedOrg}
          onSubmit={selectedOrg ? handleUpdateOrganisation : handleCreateOrganisation}
          onCancel={() => {
            setOrgModalOpen(false)
            setSelectedOrg(null)
          }}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Organisation Detail Modal */}
      <Modal
        isOpen={orgDetailModalOpen}
        onClose={() => {
          setOrgDetailModalOpen(false)
          setViewingOrg(null)
        }}
        title="Organisation Details"
        maxWidth="2xl"
      >
        {viewingOrg && (
          <OrganisationDetailModal
            organisation={viewingOrg}
            stats={orgStats[viewingOrg.id]}
            onClose={() => {
              setOrgDetailModalOpen(false)
              setViewingOrg(null)
            }}
            onEdit={() => {
              setOrgDetailModalOpen(false)
              setSelectedOrg(viewingOrg)
              setOrgModalOpen(true)
              setViewingOrg(null)
            }}
            onToggleEnabled={() => {
              setOrgDetailModalOpen(false)
              setViewingOrg(null)
              handleToggleOrgEnabled(viewingOrg)
            }}
            onViewDashboard={() => {
              setOrgDetailModalOpen(false)
              setViewingOrg(null)
              setSelectedAdminOrgId(viewingOrg.id)
              routerNavigate({ page: 'admin-dashboard', org: viewingOrg.id })
              setCurrentPage('admin-dashboard')
            }}
          />
        )}
      </Modal>

      {/* DPP Template Detail Modal */}
      <DppTemplateDetailModal
        isOpen={masterProductDetailModalOpen}
        onClose={() => {
          setMasterProductDetailModalOpen(false)
          setSelectedMasterProduct(null)
        }}
        template={selectedMasterProduct}
        onEdit={(t) => {
          setMasterProductDetailModalOpen(false)
          setSelectedMasterProduct(null)
          navigateTo('dpp-template-edit', t.id)
        }}
        onDelete={(t) => {
          setMasterProductDetailModalOpen(false)
          setMasterProductToDelete(t)
          setDeleteMasterProductModalOpen(true)
        }}
        assetCount={products.filter(p => p.masterProductId === selectedMasterProduct?.id).length}
      />

      {/* IoT Device Detail Modal */}
      <IoTDeviceDetailModal />

      {/* Delete DPP Template Confirmation */}
      <ConfirmModal
        isOpen={deleteMasterProductModalOpen}
        onClose={() => {
          setDeleteMasterProductModalOpen(false)
          setMasterProductToDelete(null)
        }}
        onConfirm={handleDeleteMasterProduct}
        title="Delete DPP Template"
        message={`Are you sure you want to delete DPP template "${masterProductToDelete?.name}"?\n\nThe template will be moved to deleted items and can be restored later. Existing assets created from this template will not be affected.`}
        confirmText="Delete Template"
        type="danger"
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  )
}
